/**
 * Request Correspondence POC panel (GitLab #1069).
 *
 * POC ONLY — hardcoded fixture, no API, no persistence, no vendor. Built so the
 * UX can be reviewed before a backend is chosen. Posting appends to local state
 * and is lost on close, by design.
 *
 * Shape follows PRD revision 2:
 *  - named threads per request, no anchoring to fields or child rows
 *  - two visibility spaces; external users see no evidence the internal one exists
 *  - internal is the DEFAULT compose destination, because a mistake there is recoverable
 *  - async only; no presence, typing indicators, or read receipts
 */
import { useQuery } from '@tanstack/react-query';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  ScrollView,
  StyleSheet,
  Modal,
  Platform,
  Pressable,
  TextInput,
  ActivityIndicator,
  LayoutAnimation,
  UIManager,
  type StyleProp,
  type ViewStyle,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { colors } from '../../theme/colors';
import { spacing } from '../../theme/spacing';
import { fontSize, fontWeight, lineHeight } from '../../theme/typography';
import { ConfirmationDialog } from '../ConfirmationDialog';
import {
  ChevronDown,
  ChevronLeft,
  ChevronUp,
  CloudOff,
  Download,
  Lock,
  MessageSquare,
  Paperclip,
  Pencil,
  Users,
  X,
} from '../icons';
import { partitionForDisplay } from './attachmentPieces';
import { withConfigTeamLabel } from './authorLabel';
import {
  COMPOSER_INLINE_IMAGE_FIELD_KEY,
  Composer,
  type ComposerMode,
} from './Composer';
import { mentionableQuery } from './correspondenceQueries';
import {
  activeSpaceOf,
  createCorrespondenceSession,
  resetSessionAfterRevoke,
  settleSentCreate,
  settleSentPost,
  useSessionField,
  type CorrespondenceSession,
} from './correspondenceSession';
import {
  attachmentSummary,
  formatListWhen,
  formatWhen,
  initials,
  nameWords,
  previewSegments,
} from './format';
import { toMentionCandidates } from './mentionCandidates';
import {
  isCoarsePointer,
  MESSAGE_ACTIONS_COARSE_OPACITY,
} from './MessageActions';
import {
  mentionTokenNames,
  messageHtmlForEditor,
  messageTextLength,
  outgoingMessage,
  reconcileInlineStaged,
  stripMentionTokens,
  toOutgoingFiles,
} from './messageHtml';
import { MessageList } from './MessageList';
import { MESSAGE_WITHDRAWN_TEXT } from './MessageRow';
// The one title bound, imported the way `Composer.tsx:68` imports it; no new
// constant (Story 7.3, "What must not change").
import {
  MAX_THREAD_FILE_LISTING,
  MAX_TITLE_LENGTH,
} from './providers/provider.types';
import {
  mayDeleteAnyMessage,
  membersForSpace,
  viewerFromUser,
  withNonMemberAuthors,
  withRequestor,
} from './team';
import { TeamStrip } from './TeamStrip';
import { ThreadFilesStrip } from './ThreadFilesStrip';
import { UNREAD_BADGE_WORD, UnreadBadge } from './UnreadBadge';
import { UNREAD_DOT_SIZE, UnreadDot } from './UnreadDot';
import { useArrivalThread } from './useArrivalThread';
import {
  OLDER_PAGE_ERROR_PREFIX,
  threadsKeyFor,
  useCorrespondence,
} from './useCorrespondence';
import { useDeviceOnline } from './useDeviceOnline';
import { useJumpToMessage } from './useJumpToMessage';
import { useMessageFileCache } from './useMessageFileCache';
import { useReduceMotion } from './useReduceMotion';
import { useRequestTeam } from './useRequestTeam';
import { MAX_DOWNLOAD_RETRIES, useSaveAllFiles } from './useSaveAllFiles';
import { useAuth } from '../../contexts/AuthContext';
import { FormKeyboardScrollProvider } from '../../contexts/FormKeyboardScrollContext';
import { useRuntimeFeatureFlags } from '../../hooks/useRuntimeFeatureFlags';
import { saveBase64File } from '../../services/attachmentService';
import {
  pickMessageFiles,
  pickerSources,
  stageInlineImageFile,
} from '../../services/correspondence';
import { fetchMessageFile } from '../../services/correspondence/messageFileBytes.fetch';
import {
  MESSAGE_FILE_CACHE_BYTES,
  admitFiles,
  capStagedThreads,
} from '../../services/correspondence/messageFileLimits';
import { canPreview } from '../../services/correspondence/messageFileViewers';
import { chooseFileSource } from '../../services/correspondence/pickSourceChooser';
import {
  PickerError,
  inlineImageReference,
  stagedFileFromHeld,
} from '../../services/correspondence/stagedFile';
import { networkService } from '../../services/network';
import { rewriteHtmlPreviewSrcsToPendingRefs } from '../../services/pendingInlineImagePreview';
import { AppRole } from '../../types/auth';
import { showConfirmation } from '../../utils/confirmation';
import {
  beginInlineImageAttempt,
  endInlineImageAttempt,
} from '../../utils/richContent/inlineAttachmentName';
import { showToast } from '../../utils/toast';
import { AttachmentCarouselModal } from '../modals/AttachmentCarouselModal';
import { RichTextKeyboardDismissBar } from '../shared/RichTextKeyboardDismissBar';
import { VisuallyHiddenText } from '../shared/VisuallyHiddenText';

import type {
  Message,
  MessageFile,
  SpaceActivity,
  SpaceId,
  SpaceUnread,
  Thread,
  ThreadFile,
} from './correspondence.types';
import type { MentionSuggestSource } from './mentionCandidates';
import type { StagedFile } from '../../services/correspondence/stagedFile';
import type {
  CarouselMedia,
  CarouselSource,
} from '../modals/AttachmentCarouselModal';
import type {
  InlineImageFile,
  InlineImageResolution,
} from '../textArea/inlineImage.types';
import type { LayoutChangeEvent } from 'react-native';

const isWeb = Platform.OS === 'web';

/**
 * KEYBOARD AVOIDANCE FOR THE OVERLAY PANEL (defect 2, 2026-09-15).
 *
 * ⚠️ WHY THIS LIVES AT THE PANEL AND NOT AT THE COMPOSER, AND WHY ANDROID HAD
 * NOTHING AT ALL.
 *
 * `Composer.tsx` used to wrap its own dock in react-native's
 * `KeyboardAvoidingView` with `behavior: 'padding'` on iOS and **`undefined`
 * on Android**, on the stated ground that the manifest already sets
 * `adjustResize`. The manifest does set it (`AndroidManifest.xml:42`) — but
 * the overlay panel renders inside a `Modal`, and **an RN `Modal` is its own
 * window: it does not inherit the activity's `windowSoftInputMode`.** So the
 * Android branch relied on a resize that never happened, and the keyboard
 * covered whichever composer field had focus. All four composer modes
 * (`idle | compose | reply | edit`) share one dock, which is why one mechanism
 * produced three separate reports: the edit field, the new conversation's
 * subject, and its first message.
 *
 * The same window boundary is why a provider mounted at the app root is not
 * enough. React context crosses a `Modal` (it is one React tree) but the
 * native view hierarchy does not, so the panel's Modal gets **its own**
 * `KeyboardProvider` — exactly what `RepeatableInstanceEditModal.tsx:21-35`
 * does, and for the same reason.
 *
 * Avoidance belongs at the PANEL because the panel owns the column: the
 * message list is the one `flex: 1` child, so padding the column bottom makes
 * the LIST give up the space and the composer ride above the keyboard. The
 * composer could only ever have padded itself.
 *
 * Loaded through a guarded `require` like `App.tsx:256-263` and
 * `RepeatableInstanceEditModal`, so web never pulls in a native module and a
 * missing module degrades to "no avoidance" rather than a crash.
 */
type PanelKeyboardModule = {
  KeyboardProvider: React.ComponentType<{ children: React.ReactNode }>;
  KeyboardAvoidingView: React.ComponentType<{
    behavior?: 'height' | 'padding' | 'position';
    style?: StyleProp<ViewStyle>;
    children?: React.ReactNode;
  }>;
  useKeyboardState: <T>(selector: (state: { isVisible: boolean }) => T) => T;
};

let panelKeyboard: PanelKeyboardModule | null = null;
if (!isWeb) {
  try {
    panelKeyboard =
      require('react-native-keyboard-controller') as PanelKeyboardModule;
  } catch (error) {
    console.warn(
      'Correspondence panel: keyboard controller unavailable',
      error,
    );
    panelKeyboard = null;
  }
}

/**
 * The panel's column on native, with the keyboard's height taken out of it.
 *
 * ⚠️ THE BOTTOM INSET MOVES IN HERE, AND THAT IS THE POINT OF THE COMPONENT.
 * The panel root pads itself by `insets.bottom` on iOS so the composer clears
 * the home indicator. With the keyboard up that padding is on top of the
 * keyboard's own height, and the two stack into a visible gap over the
 * keyboard. The keyboard already covers the indicator, so the inset applies
 * only while the keyboard is down.
 *
 * Rendered ONLY on native and ONLY for the overlay presentation, so the hooks
 * below are never reached on web and the docked panel gains no layout node
 * (the docked panel is web-only, and contains no `Modal` by standing rule).
 */
const PanelKeyboardColumn: React.FC<{
  bottomInset: number;
  children: React.ReactNode;
}> = ({ bottomInset, children }) => {
  // Non-null by construction: the only caller checks `panelKeyboard` first.
  const keyboard = panelKeyboard as PanelKeyboardModule;
  const keyboardVisible = keyboard.useKeyboardState(state => state.isVisible);
  const { KeyboardAvoidingView } = keyboard;
  // Built here rather than inline so the style array stays a stable pair and
  // the house `no-inline-styles` rule has nothing to say about it.
  const insetStyle = useMemo(
    () => ({ paddingBottom: keyboardVisible ? 0 : bottomInset }),
    [keyboardVisible, bottomInset],
  );
  return (
    <KeyboardAvoidingView behavior="padding" style={styles.keyboardColumn}>
      <View
        style={[styles.keyboardColumn, insetStyle]}
        testID="correspondence-panel-keyboard-inset"
      >
        {children}
      </View>
    </KeyboardAvoidingView>
  );
};

/**
 * The same sentence on both submit paths, because it is the same situation:
 * the message is still yours, still here, and one press from being sent. It
 * deliberately does not name the vendor or the space — it is shown to whoever
 * was typing, including a customer.
 */
/**
 * "Remove @Priya Shah and send", "Remove @A and @B and send", "Remove @A, @B
 * and @C and send" (#1599 Story 1.4, AC 9). While editing, "Remove @Priya
 * Shah and save" (#1599 Story 1.8, AC 11): an edit that ADDS someone outside
 * the audience is refused like a post, and the same action re-saves the edit
 * (`postDraft` branches on `editing`), beside a button that reads Save.
 */
export const removeRefusedLabel = (
  names: readonly string[],
  verb: 'send' | 'save' = 'send',
): string => {
  // Two refused people can carry one display name; say it once.
  const tokens = [...new Set(names)].map(name => `@${name}`);
  const joined =
    tokens.length <= 1
      ? tokens.join('')
      : `${tokens.slice(0, -1).join(', ')} and ${tokens[tokens.length - 1]}`;
  return `Remove ${joined} and ${verb}`;
};

const UNSENT_POST =
  'Your message has not been sent yet. It is still in the box below — press Post to try again.';
/**
 * One shared empty array for every thread with nothing staged, so a thread that
 * has never been picked into does not hand `Composer` a new `[]` on every
 * render and re-run its memoized children.
 *
 * ⚠️ `readonly` AND FROZEN (code review run 3, 2026-09-13). ONE array instance
 * is handed to `Composer` for every thread with nothing staged. Nothing mutates
 * it today — `admitFiles` and `filter` both copy — but the type permitted it,
 * and a single in-place `push` would leak one thread's files into every other
 * empty thread: precisely the audience bug the thread-keying was introduced to
 * fix, re-entering through the empty case.
 */
const EMPTY_STAGED: readonly StagedFile[] = Object.freeze([]);

/**
 * The staging keys that are not a thread id (#1530). A thread id is digits
 * from the server, so neither of these can collide with one. `stagedByThread`
 * keeps its name: it is still a map of strips, and `capStagedThreads` bounds
 * every key alike.
 */
const COMPOSE_STAGING_KEY = 'compose';
const editStagingKey = (messageId: string): string => `edit:${messageId}`;

/** The resolver's refusal when the editor has nowhere to put a picture. */
const NO_COMPOSER_FOR_IMAGE = 'Open a conversation to add a picture.';
const IMAGE_UNREADABLE = 'That image could not be read.';

// `MAX_DOWNLOAD_RETRIES` lives in `useSaveAllFiles.ts` since Story 7.5 (D13):
// one definition for the single save and the zip.

/**
 * The same situation when files rode on the post (#1069 Story 4.1, AC 13).
 *
 * ⚠️ IT NAMES THEM BECAUSE THEY CAME BACK TOO. "Your message is still here" is
 * quietly wrong when three photos are also sitting in the composer, and a user
 * who cannot see that the files survived will re-attach them.
 */
const unsentPostWithFiles = (n: number) =>
  `Not sent. Your message and ${n} ${
    n === 1 ? 'file' : 'files'
  } are still here — press Post to try again.`;
/**
 * The last byte left and the server never answered (#1069 Story 6.7, D4). NOT
 * the unsent line: "Not sent" would be a guess, and the wrong guess sends it
 * twice. The strip carries MAY_ALREADY_BE_SENT beside the files for the same
 * reason.
 */
const unconfirmedPost = (n: number) =>
  `The server did not confirm your message. It may already be in the conversation, so check before posting again. ${
    n === 0
      ? 'Your message is'
      : `Your message and ${n} ${n === 1 ? 'file' : 'files'} are`
  } still here.`;
/**
 * The draft as it goes on the wire (#1530 review, C3): preview srcs mapped
 * back to their refs FIRST, then trimmed. The web editor emits refs, but the
 * native editor shows a data URI for a staged or held image and may never
 * re-emit before Send (its persist pass runs on change), so a `draft` holding
 * `data:` srcs is a real shape. `outgoingMessage` only rewrites refs, and a
 * data URI left in the body would be sent as markup: refused as too much
 * formatting at best, a copy of the picture the message already holds at
 * worst. The exact-map reverse is the editors' own, so this is the same
 * mapping they would have applied.
 */
const wireDraft = (draft: string): string =>
  rewriteHtmlPreviewSrcsToPendingRefs(draft).trim();

/** The same situation on the create path, where the button reads Send (review). */
const UNSENT_SEND =
  'Your message has not been sent yet. It is still in the box below — press Send to try again.';
/** And on the edit path, where it reads Save. */
const UNSENT_SAVE =
  'Your correction has not been saved yet. It is still in the box below — press Save to try again.';

/** A refused withdrawal; the message is still there (Story 1.3). */
const UNSENT_DELETE = 'The message could not be deleted. It is still here.';

/**
 * The tab changed while the conversation was being created (Story 1.3). The
 * conversation AND its first message were sent, in the tab the user was on
 * when they pressed Send, so nothing is lost; the panel does not auto-open a
 * conversation from a tab the user has left (that would put it under the
 * other tab's audience notice). Names no space: only a two-tab viewer can
 * reach this, but the copy stays audience-neutral anyway.
 */
/**
 * What the panel says when a jump from the files strip cannot reach its
 * message (#1069 Story 7.6, D9): the beginning of the conversation was reached
 * without it, a page repeated itself, or `MAX_JUMP_PAGES` were pulled. Said,
 * not swallowed -- a press that does nothing is a dead control, the defect the
 * owner hit on a `.pptx` in 7.4. Rendered on the viewer's line, as its own
 * state.
 */
export const JUMP_UNREACHABLE = 'That message could not be reached.';

const SENT_IN_PREVIOUS_TAB =
  'Your message was sent in the conversation you started before switching tabs. Switch back to see it.';

/**
 * What the rename line says when the server refused WITHOUT an explanation
 * (Story 7.3, D8). A 400's reason and a 403's explanation are the server's and
 * are shown verbatim through `writeRefusal`; this is the fallback for
 * everything else.
 */
export const RENAME_FAILED_TEXT = 'Could not rename the conversation';

/**
 * The delete confirmation (Story 1.3, AC 28).
 *
 * ⚠️ AUTHOR-NEUTRAL, DELIBERATELY (code review, 2026-09-11). C10 tied this
 * copy to `MESSAGE_WITHDRAWN_TEXT` so the promise and the rendered line stay
 * one source, which is right; the first spelling of it said "a line saying
 * YOU withdrew a message" and the rendered line names the ORIGINAL AUTHOR
 * (`withdrawnLine`, `MessageRow.tsx`), which is a different person on the
 * two paths this one dialog serves: an IT Administrator deleting somebody
 * else's message (`mayDeleteAny`) was promised their own name and shown
 * Ann Lee's, and even on the viewer's own message the line renders the
 * display name while the group label two lines away says `You`. "who" is
 * true on every path and keeps the single source.
 */
const DELETE_MESSAGE_COPY = `This cannot be undone. Anyone who has already read the message, or received it by email, has seen it. A line saying who ${MESSAGE_WITHDRAWN_TEXT} will stay in its place.`;

interface Props {
  visible: boolean;
  onClose: () => void;
  /**
   * 'docked' renders inline as a flex sibling, so the request content is
   * pushed left and shrinks. 'overlay' renders in a Modal over the screen.
   */
  presentation?: 'overlay' | 'docked';
  requestLabel?: string;
  requestId?: string;
  requestorEmail?: string;
  requestorName?: string;
  /**
   * FR54, per space, so the space TABS can carry a dot (#1069 P3.2).
   *
   * ⚠️ WITHOUT IT THE TOGGLE'S DOT IS A PROMISE THIS PANEL BREAKS. The toggle
   * unions across every space the caller may read; this panel then opens on
   * `internal` (below). Unread that lives in SHARED therefore lights the toggle
   * and resolves to an apparently empty screen, and the user concludes the dot
   * lied. The tab dot is not a fourth level — it is the FR54 signal
   * disambiguated, and it exists only because the toggle aggregates across a
   * boundary the panel then makes the user choose.
   *
   * Supplied by the screen from the summary endpoint. `undefined` renders no
   * tab dots, which is right: the tab dot exists to STEER, and a guess is worse
   * than silence.
   */
  spaceUnread?: SpaceUnread;
  /**
   * The per-space CLOCKS off the summary (Story 2.3, D1; FR69): when
   * anything in each audience last moved for this viewer, as ISO strings.
   * The screen's summary ticks every 10 s while this panel is open; when the
   * clock for the space ON SCREEN moves from a value already seen, the panel
   * re-lists that space through the provider it already has, and the rows
   * come back with the server's per-person `unread` and their own clocks, so
   * the row dot and the tab badge light with nobody pressing anything. If
   * the open conversation is one of the rows that moved, its newest page is
   * refetched (`refreshOpenThread`, newest page only). Supplied by the
   * screen; `undefined` means no clock yet, and nothing is re-asked on its
   * first arrival; `null` after a value means the server degraded to the
   * empty summary (or the audience emptied), and is read as no information.
   */
  spaceActivity?: SpaceActivity;
  /**
   * The summary answered 404 (Story 2.3, D3): the server has stopped
   * answering this person about this request (removed from the team while
   * the panel sat open). The panel stops asking, closes the open thread and
   * renders its list region EMPTY: no rows, no error text, no composer, no
   * badges. Not "No discussions yet", which is a statement about a request
   * the person may read. The screen refuses on the next navigation.
   */
  accessRevoked?: boolean;
  /**
   * The panel reporting what it can see for itself, per space (#1069 P3.3).
   *
   * ⚠️ WITHOUT IT THE LAUNCHER PILL CANNOT BE RIGHT. That pill lives outside
   * this panel and renders the summary endpoint's answer; the panel is where
   * the user's read actually happens. Reported up so the SCREEN can merge the
   * two once and hand the merged value to every surface — see
   * `useUnreadSpaceState.ts`, which also records why refetching the summary
   * does not close this on its own.
   *
   * Optional, and absence is silent: the panel still works, and the surfaces
   * outside it stay on the server's answer.
   */
  onSpaceUnreadKnown?: (space: SpaceId, hasUnread: boolean) => void;
  /**
   * Fired after the panel marks a thread read (#1069 P3.2), so the screen can
   * invalidate the summary AND the request list. See `RequestDetail` for why
   * the list matters: `useRequestsQuery` sets `staleTime: 30 * 1000`.
   */
  onThreadRead?: () => void;

  /**
   * Called when a composer text field takes or loses focus (defect 3,
   * 2026-09-15). Only the DOCKED host passes it: on web the panel and the
   * request share one scroll view, whose `scroll-padding-bottom` makes every
   * keystroke in the composer ask that scroller to reveal a caret it can never
   * reveal, because the panel is sticky. The host stands the allowance down
   * while this is true. See `Composer`'s `composerFieldFocused` note.
   */
  onComposerFocusChange?: (focused: boolean) => void;

  /**
   * "Where I was", owned by the SCREEN (#1069, owner rulings 2026-09-25/26):
   * the tab, the conversation, the draft, the staged files, an edit or a new
   * conversation in progress, a send in flight and its warnings. Both
   * production mounts (the docked one and the launcher's overlay) receive the
   * SAME session, which is what lets a resize swap them and a close unmount
   * the docked one without losing any of it. See `correspondenceSession.ts`.
   * Omitted only by a suite mounting the panel alone, which gets a session
   * of its own for the mount's life: what a lone mount always had.
   */
  session?: CorrespondenceSession;
}

/**
 * A file the viewer can open, and the message it came from (#1069 Story 7.4,
 * D4).
 *
 * ⚠️ THE `messageId` IS WHAT MAKES A CONVERSATION-WIDE GALLERY POSSIBLE. Until
 * Story 7.4 every gallery came from one bubble, so the open set carried ONE
 * `Message` and every fetch went through its id. The files strip's gallery
 * spans the conversation, so stepping to the next image can cross into another
 * message — and a set keyed to one message would 404 on every step but the
 * first. This is exactly the shape the listing's `ThreadFile` already has, so
 * the strip's items pass straight through and the bubble's are stamped at
 * `openAttachment`.
 */
type OpenableFile = MessageFile & { messageId: string };

/**
 * A message file as the carousel sees it (GitLab #1539), still carrying the
 * message it rides on and the file it was made from, for the loader and the
 * unsupported page's Download.
 */
type GalleryItem = CarouselMedia & { messageId: string; file: OpenableFile };

const toGalleryItem = (file: OpenableFile): GalleryItem => ({
  id: file.id,
  name: file.name,
  mimeType: file.mimeType,
  sizeBytes: file.sizeBytes,
  thumbnailUri: file.thumbnailUri,
  // ⚠️ THE STORED (SNIFFED) TYPE decides this, never the name (Story 4.1).
  previewable: canPreview(file),
  messageId: file.messageId,
  file,
});

export const CorrespondencePanel: React.FC<Props> = ({
  visible,
  onClose,
  presentation = 'overlay',
  requestLabel = 'Request',
  requestId = 'poc',
  requestorEmail,
  requestorName,
  spaceUnread,
  spaceActivity,
  accessRevoked = false,
  onSpaceUnreadKnown,
  onThreadRead,
  onComposerFocusChange,
  session: screenSession,
}) => {
  /**
   * On iOS the overlay is a full-window Modal that starts at y=0, so the
   * header sat under the clock and the Dynamic Island and, on a
   * home-indicator device, the composer ran under the indicator. The header
   * was visible and, as observed on the simulator, its controls could not be
   * tapped. Pad the root by the live insets and let the panel's own
   * background paint up behind the clock, as `Header` does for the page.
   *
   * iOS ONLY, on purpose. On Android a Modal that does not opt into
   * `statusBarTranslucent` already fits below the status bar, in every
   * flavor, while `FlavorBanner` makes the app's own status bar translucent
   * off PROD so the hook reports a real top inset there: padding would stack
   * on top of the fit and open a status-bar-high gap. `AttachmentCarouselModal`
   * is the repo's other full-screen Modal; it opts in and pads Android by
   * `StatusBar.currentHeight` itself. The panel does not need to opt in, so
   * Android keeps the platform fit and nothing here changes for it.
   *
   * Overlay only. The docked panel is web-only (`shouldDock`), where every
   * inset is zero anyway; the branch keeps that explicit.
   */
  const insets = useSafeAreaInsets();
  // Read at render, not at module load, so the platform branch is testable
  // the way `Composer` reads `Platform.OS` (the suite swaps it per test).
  //
  // ⚠️ SPLIT IN TWO ON 2026-09-15 (defect 2). The TOP inset stays on the panel
  // root, where it has always been. The BOTTOM inset moves inside
  // `PanelKeyboardColumn`, because it must not apply while the keyboard is up:
  // the keyboard already covers the home indicator, and padding for both
  // stacks into a gap above the keyboard. Android is unchanged (no inset at
  // all, for the documented reason above) and so passes 0.
  const overlayInsetStyle =
    Platform.OS === 'ios' ? { paddingTop: insets.top } : null;
  const overlayBottomInset = Platform.OS === 'ios' ? insets.bottom : 0;
  const [ownSession] = useState(createCorrespondenceSession);
  const session = screenSession ?? ownSession;
  const [space, setSpace] = useSessionField(session, 'space');
  /**
   * Whether the DEVICE reports a connection (#1069 offline state,
   * 2026-09-25). Messaging is deliberately online-only (spec-1069 §11.1, no
   * queue, no replay), and offline is a calm state here, not an error:
   * whatever is already in memory stays on screen read-only, a neutral empty
   * state stands where nothing is loaded, every write and save control is
   * hidden, every error line is held back, and the panel reloads by itself on
   * reconnect. ⚠️ `useDeviceOnline`, NOT `useNetworkStatus`: see that file,
   * each instance of the latter runs an app-wide `fullSync()` on reconnect.
   */
  const online = useDeviceOnline();
  const [draft, setDraft] = useSessionField(session, 'draft');
  /**
   * Files chosen for the message being written (#1069 Story 4.1, AC 2).
   *
   * ⚠️ HELD BY THE PANEL, NOT THE COMPOSER, for the same reason `draft` is: the
   * panel is what survives a failed or cancelled send and has to hand the
   * message back intact (AC 12, AC 13). A composer that owned them would lose
   * them on any remount.
   *
   * ⚠️ AND BOUND TO THE THREAD THEY WERE CHOSEN FOR (code review 2026-09-13).
   * `draft` is deliberately global — leaving a thread is not a discard action
   * and typed text is not thrown away — but a FILE is not text. Files staged
   * while reading an internal thread used to survive `leaveOpenThread`, which
   * the space tabs go through, so they could be posted into the shared,
   * customer-visible space; and they went on rendering in the compose shape,
   * whose paperclip does not exist because a first message cannot carry files.
   * Keying them by thread makes the wrong audience unreachable rather than
   * remembered, and costs nothing the user would want: leave and come back and
   * they are still here.
   */
  /**
   * ⚠️ A `Map`, AND THE ORDER IS LOAD-BEARING (code review run 3, owner option
   * (b), 2026-09-13). This was a `Record`, which had NO aggregate ceiling and
   * was never purged — three threads staged meant up to 120 MB of base64 held
   * on a phone. `capStagedThreads` evicts the least-recently-touched strips,
   * and "least recently touched" is the `Map`'s own insertion order, which
   * `setStagedForThread` maintains by deleting the thread before re-setting it.
   * An object's key order is not usable for this: numeric-looking thread ids
   * would sort ahead of the rest.
   */
  const [stagedByThread, setStagedByThread] = useSessionField(
    session,
    'stagedByThread',
  );

  /**
   * The thread whose strip survived a CANCEL that may have committed anyway
   * (owner decision, code review run 3, 2026-09-13). Keyed by thread for the
   * same reason the strip itself is: the caution belongs to one conversation.
   * Cleared the moment the strip changes, because re-picking or removing a file
   * is the user taking charge of it.
   */
  const [mayAlreadyBeSent, setMayAlreadyBeSent] = useSessionField(
    session,
    'mayAlreadyBeSent',
  );

  /**
   * The set the gallery is showing, and where it opened (#1069 Story 4.2
   * AC 7; GitLab #1539).
   *
   * ⚠️ ONE MODAL FOR EVERY FILE KIND, AND IT OWNS ITS OWN LOADING (owner
   * ruling 2026-10-06). Until #1539 the panel held an index, a loading flag,
   * a generation counter and a "metadata only" flag, and drove THREE dialogs
   * from outside -- the media viewer for pictures (with paging bolted on),
   * the PDF viewer for PDFs, and the media viewer's unsupported state for
   * everything else -- because none of them could take a set. The carousel
   * takes the set, pages it, loads current ±1 through `gallerySource` and
   * shows a per-item error, so the panel keeps only what it opened.
   *
   * Every item carries ITS OWN `messageId` (Story 7.4, D4): a set is one
   * bubble's files, but the loader still fetches each by the id it rides on.
   */
  const [gallery, setGallery] = useState<{
    items: GalleryItem[];
    initialIndex: number;
  } | null>(null);

  const [newTitle, setNewTitle] = useSessionField(session, 'newTitle');
  const [composingThread, setComposingThread] = useSessionField(
    session,
    'composingThread',
  );
  const [submitting, setSubmitting] = useSessionField(session, 'submitting');
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  /**
   * The unsent line, owned by the panel: the generic provider error is racy
   * here (landing in a new thread starts a message load that clears it), so
   * the "your message has not gone yet" line sits beside the box still holding
   * the text and says what the one recovery action is. When the hook holds the
   * server's own explanation for the refusal (`writeRefusal`, Story 1.3 AC
   * 37), the line shows that instead.
   */
  const [sendError, setSendError] = useSessionField(session, 'sendError');
  /**
   * The message being corrected in the ONE composer (Story 1.3, D2), or
   * `null`. The draft that was in the box before the edit began is kept and
   * restored on Cancel. All three are session fields beside `draft`, so a
   * resize or a close mid-edit is still an edit (2026-09-25); the two that
   * were refs keep their `{ current }` shape and still never render.
   */
  const [editing, setEditing] = useSessionField(session, 'editing');
  const draftBeforeEdit = session.refFor('draftBeforeEdit');
  /** What the composer was set to when the edit began: unchanged means nothing to lose. */
  const editPrefill = session.refFor('editPrefill');
  /**
   * The cancel-edit confirmation (review, D2): the action to run once the
   * user confirms, or `null` while nothing is being asked. Mirrors
   * `cancelCompose`: asked only when there is something to lose.
   */
  const [confirmCancelEdit, setConfirmCancelEdit] = useState<
    (() => void) | null
  >(null);
  /** The message awaiting the delete confirmation (Story 1.3, AC 28, 36). */
  const [confirmDelete, setConfirmDelete] = useState<Message | null>(null);

  // ==========================================================================
  // Renaming the open conversation (#1069 Story 7.3, FR74).
  // ==========================================================================
  /** The in-progress new name, or `null` while the editor is closed. */
  const [renameDraft, setRenameDraft] = useState<string | null>(null);
  /**
   * Why the last rename failed.
   *
   * ⚠️ ITS OWN CHANNEL (D8). The panel's error slots each have an owner --
   * `sendError` the composer's unsent line, `error` the hook's bar, `jumpError`
   * the strip's -- and the 2026-09-14 review is the record of what happens
   * when one borrows another's. (A fourth, `openError` for a failed file open,
   * retired with GitLab #1539: the gallery reports a failure on the page
   * itself.) Borrowing `sendError` here would print "Not sent. Your message
   * is still here" at somebody who sent nothing.
   */
  const [renameFailed, setRenameFailed] = useState(false);
  /** One commit at a time: pressing Save during a commit is a no-op (D6). */
  const [renameCommitting, setRenameCommitting] = useState(false);
  /**
   * Whether the rename field takes focus (and selects its text) as it mounts.
   * Only a press on the pencil asks for that: an editor that comes back by
   * itself on reconnect (#1069 offline state) must not pull the keyboard up.
   */
  const [renameFocus, setRenameFocus] = useState(false);
  /** The reveal (D10), copying `MessageActions`' formula exactly. */
  const [headerHovered, setHeaderHovered] = useState(false);
  const [pencilFocused, setPencilFocused] = useState(false);
  /** Once per mount: a device does not change pointer class mid-conversation. */
  const [coarsePointer] = useState(isCoarsePointer);

  /**
   * THE ONE MEASUREMENT (#1069 UI-8): the height of the region that holds the
   * message list and the composer.
   *
   * ⚠️ IT IS ONE CONTAINER, MEASURED ONCE, IN ONE COMPONENT, AND THE DRAG
   * CANNOT CHANGE IT. The region is a `flex: 1` child of the panel's column, so
   * its height is `panel − header − tabs − banner − error bars` and nothing
   * else. The composer's drag redistributes space BETWEEN the list and the
   * field inside it; both are children of this box and neither can resize the
   * box.
   *
   * That is the whole reason this is safe, and it is why UI-7's version was
   * not: its maximum was `listHeight + fieldHeight`, two children measured in
   * two components, both moved by the drag it was bounding. See the header of
   * `composerLayout.ts` for the four defects that produced.
   *
   * It also gives "everything below the header" for free, with nothing
   * enumerated: the fixed chrome is above the region by construction, so
   * nothing has to be kept in sync with it.
   *
   * 0 until the first layout, which `composerMaxHeight` reads as "no
   * measurement" and answers with the pre-UI-8 220.
   */
  const [composerRegionHeight, setComposerRegionHeight] = useState(0);

  /**
   * Whether the files strip is open (#1069 Story 7.4, D11).
   *
   * ⚠️ THE PANEL OWNS IT, NOT THE STRIP. It has to be reset from
   * `leaveOpenThread`, which is where the rename editor's reset already is,
   * and a component that unmounts when it closes cannot remember anything to
   * reset anyway. The DATA is not gated on this -- the listing is fetched when
   * the conversation opens, because the count IS the list's own (D1, C1); only
   * the revealing is lazy.
   */
  const [filesExpanded, setFilesExpanded] = useState(false);
  const reduceMotion = useReduceMotion();
  /**
   * The Files drawer SLIDES on native (owner, Story 7.6 walk, 2026-09-19:
   * "it just appears, it should feel native"). `LayoutAnimation` animates the
   * next layout pass -- the strip growing in, the list giving way -- without
   * keeping the strip mounted during an exit, so every "strip absent" pin
   * and the row's `Expand files` label stay exactly as they are. Web keeps
   * its instant toggle (no LayoutAnimation on react-native-web) and a reader
   * who asked for less motion gets none. Optional-called: the shared test
   * mock has no `LayoutAnimation`, and animating is never load-bearing.
   */
  const animateFilesDrawer = useCallback(() => {
    if (Platform.OS === 'web' || reduceMotion) return;
    const layoutAnimation = LayoutAnimation as
      | typeof LayoutAnimation
      | undefined;
    const preset = layoutAnimation?.Presets?.easeInEaseOut;
    if (!preset || typeof layoutAnimation?.configureNext !== 'function') {
      return;
    }
    if (Platform.OS === 'android') {
      // A no-op on the new architecture; still required by the old one.
      (
        UIManager as
          | { setLayoutAnimationEnabledExperimental?: (on: boolean) => void }
          | undefined
      )?.setLayoutAnimationEnabledExperimental?.(true);
    }
    layoutAnimation.configureNext(preset);
  }, [reduceMotion]);
  /**
   * The jump's failure line (Story 7.6, D9), held as its OWN state so the
   * channels' owners stay legible (`sendError` the composer, `error` the
   * hook). Cleared on the next strip press, on collapse, and in
   * `leaveOpenThread`.
   */
  const [jumpError, setJumpError] = useState<string | null>(null);
  const onComposerRegionLayout = useCallback((event: LayoutChangeEvent) => {
    const next = Math.round(event.nativeEvent.layout.height);
    // Guarded against re-setting an identical value, not because React would
    // loop on one, but because a fractional layout can otherwise oscillate
    // between two roundings.
    setComposerRegionHeight(prev => (prev === next ? prev : next));
  }, []);

  /**
   * Bumped ONLY where a send has been confirmed by the provider (#1069 UI-8).
   *
   * The composer resets its dragged height when this changes, restoring the
   * default and re-enabling auto-grow exactly as opening the panel does. Every
   * early return in `post` and `createThread` — the provider said no, the
   * thread could not be created, the space changed under the send — leaves it
   * untouched, because in all of those the message is still in the box and
   * still the user's to work on.
   *
   * A counter rather than a boolean: two successful sends in a row must both
   * reset, and a boolean would only ever fire once.
   */
  const [sendSucceeded, setSendSucceeded] = useState(0);
  const noteSendSucceeded = () => setSendSucceeded(n => n + 1);
  /**
   * #1530: when the composer's editor must be seeded again from the draft
   * (an edit begins or ends, a compose is discarded or sent). A send that
   * worked bumps `sendSucceeded`, which the composer also remounts on.
   */
  const [bodySeed, setBodySeed] = useState(0);
  const bumpBodySeed = () => setBodySeed(n => n + 1);
  /**
   * The keyboard-chrome provider wants a scroll ref it can reveal a field
   * in; this panel's column already avoids the keyboard, so there is nothing
   * to reveal and the ref stays empty.
   */
  const inertScrollRef = useRef<ScrollView | null>(null);

  /**
   * The double-submit guard has to be a REF, not the `submitting` state.
   *
   * Two taps in the same tick both read the state value captured at render, so
   * a state flag (and the `disabled` prop derived from it) lets the second one
   * through and creates a second thread. The ref is written synchronously and
   * closes that window; the state still drives `disabled`, for the user.
   *
   * ⚠️ A SESSION FIELD SINCE 2026-09-25, READ THROUGH THE SAME `{ current }`.
   * The store writes synchronously, so the one-tick guarantee holds; and a
   * send outlives the mount that started it, so a mount created by a resize
   * mid-send reads it as in flight instead of offering a second Post.
   */
  const submittingRef = session.refFor('writeInFlight');

  const { user } = useAuth();
  // `loading` drives NO spinner: the team resolves quickly and the thread list
  // has its own, so a second indicator only adds flicker. It gates one piece of
  // TEXT, the header's people count (Story 5.4 C11, code review 2026-09-11).
  const {
    members: teamMembers,
    loading: teamLoading,
    error: teamLoadError,
    reload: reloadTeam,
  } = useRequestTeam(requestId, visible && !accessRevoked);
  // D3: nothing is shown for a request the person no longer has, a stale
  // team-load failure included.
  const teamError = accessRevoked ? null : teamLoadError;

  /**
   * The viewer is ALWAYS the current user — the impersonated user during an
   * impersonation session, since AuthContext swaps the JWT and refreshes.
   *
   * Internal vs external comes from that user's OWN app roles, never from a
   * lookup against the request team. An external requestor who created the
   * request is typically absent from `team_information`, and an earlier version
   * of this fell back to `members[0]` on a lookup miss — which handed an
   * impersonated external requestor the sponsor's identity and the internal
   * tab. A fallback that selects a MORE privileged identity is the exact
   * inversion of failing closed.
   */
  // Memoised on primitives. Rebuilding these every render made the hook's
  // callbacks unstable and produced a request loop against Stream; the roles
  // array is joined for the same reason.
  const roleKey = (user?.roles ?? []).join(',');
  // `user.id` is the numeric `users.user_id` as a string (Story 1069-1.1); it
  // is what `Message.authorId` carries, so it is the only thing that can make
  // a message "mine".
  const viewer = useMemo(
    () => viewerFromUser(user?.id, user?.email, user?.name, user?.roles),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user?.id, user?.email, user?.name, roleKey],
  );

  // The requestor is not a team_information row, so fold them in explicitly or
  // the customer cannot see the shared threads written for them.
  //
  // ⚠️ THE VIEWER IS NOT FOLDED IN (2026-10-06; `withViewer` removed). The
  // list is the request team plus the requestor, exactly the server's
  // projection; a viewer with no team row is listed only through their own
  // messages in the open conversation (`withNonMemberAuthors`, below). The
  // viewer still goes into `withRequestor`, so a requestor who IS the viewer
  // takes their own standing rather than a guess (#1069 Story 8.2).
  const members = useMemo(
    () => withRequestor(teamMembers, requestorEmail, requestorName, viewer),
    [teamMembers, requestorEmail, requestorName, viewer],
  );

  // An external viewer can never be in the internal space.
  const canSeeInternal = viewer?.isInternal ?? false;

  /**
   * #1599 Story 1.7 (AC 11): the viewer holds Configuration Engineer, so the
   * team token ("@Vertiv Config Team") is theirs and is tinted as their own.
   * EXACT `AppRole` match on the JWT's roles, like `mayDeleteAnyMessage` and
   * the server's `writesAsConfigTeam`; never the normalized classifier, which
   * is for role names read from SQL. An unidentified viewer tints nothing.
   * `viewer.isInternal` mirrors the server's external veto (M33): an engineer
   * who also holds an external role gets no fan-out row, so no tint either
   * (code review 2026-10-22). The author is excluded per row, in `MessageRow`.
   * Memoised on `roleKey`, the primitive the viewer is memoised on.
   */
  const viewerIsConfigTeam = useMemo(
    () =>
      !!viewer?.isInternal &&
      (user?.roles ?? []).includes(AppRole.CONFIG_ENGINEER),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [viewer, roleKey],
  );

  /**
   * Whether the header may say how many people can read the active space
   * (Story 5.4, AC 3; C11). `members` folds the requestor in SYNCHRONOUSLY
   * (`withRequestor`, above), so `spaceMembers.length` can already be 1 before
   * the team call resolves and after it fails — a count of "1 person" that
   * flips to "3 people" a moment later would be the list guessing out loud
   * (code review 2026-09-11). The label stands alone until the team has
   * actually been read, on a load error, and after a revoke.
   */
  const teamCountKnown = !teamLoading && !teamLoadError && !accessRevoked;
  // The strip's three-way reading of the same facts: a boolean could not tell
  // "still loading" from "failed", and said "Loading" forever on an error.
  const teamStatus: 'loading' | 'unavailable' | 'ready' = teamLoading
    ? 'loading'
    : teamLoadError || accessRevoked
    ? 'unavailable'
    : 'ready';

  /*
   * ⚠️ NO READ-ONLY DERIVATION AND NO STAGE (Story 1.3, C7, FR30). The panel used to
   * derive a closed notice from the request stage and withhold the composer on a
   * CLOSED request. Stage never participates in a write decision any more, on
   * the server or here: the composer renders on every request, and a refusal
   * is the server's to make and the unsent line's to show.
   */
  const activeSpace: SpaceId =
    space === 'internal' && !canSeeInternal ? 'shared' : space;
  /**
   * The viewer's standing, for a create that lands in another mount: with
   * `space` it is the effective space (`activeSpaceOf`), which is what that
   * create compares. Written by every mounted panel; they all derive it from
   * the same user.
   */
  useEffect(() => {
    session.set('canSeeInternal', canSeeInternal);
  }, [session, canSeeInternal]);

  /**
   * The open conversation and its mark-read token, from the session.
   *
   * ⚠️ A REVOKED READER HAS NO OPEN CONVERSATION FROM THE FIRST RENDER
   * (review, 2026-09-26). The session keeps the thread through a close, so a
   * docked panel reopened after a revoke mounted onto it, and its first
   * render asked for the pages and the file listing before the revoke effect
   * (below) could clear the session. Derived here, the hook never sees it.
   */
  const [sessionThreadId, setSessionThreadId] = useSessionField(
    session,
    'openThreadId',
  );
  const sessionOpenThread = {
    openThread: [
      accessRevoked ? null : sessionThreadId,
      setSessionThreadId,
    ] as const,
    openThreadNow: session.refFor('openThreadId'),
    markedToken: session.refFor('markedToken'),
    threadCacheKeys: session.refFor('threadCacheKeys'),
    accessRevoked: !!accessRevoked,
    onWriteRefusal: session.setterFor('writeRefusal'),
    onRefusedMentionIds: session.setterFor('refusedMentionIds'),
  };
  /**
   * The refusal the unsent line shows, from the session: the write it
   * explains may have failed in a mount a resize retired (AC 37).
   */
  const [writeRefusal] = useSessionField(session, 'writeRefusal');
  /**
   * The ids the last refusal named (#1599 Story 1.4, AC 9), from the session
   * for the reason `writeRefusal` is: the editor outlines those tokens and
   * the unsent line offers to remove them.
   */
  const [refusedMentionIds, setRefusedMentionIds] = useSessionField(
    session,
    'refusedMentionIds',
  );
  const {
    provider: messagingProvider,
    threads,
    threadsKey,
    messages,
    threadFiles,
    olderMessagesCursor,
    loadingThreads,
    loadingMessages,
    loadingOlder,
    loadOlderMessages,
    error: loadError,
    pending,
    openThread: setOpenThreadId,
    openThreadId,
    createThread: createThreadOnProvider,
    postMessage,
    cancelPending,
    editMessage,
    deleteMessage,
    renameThread,
    reloadThreads,
    refreshOpenThread,
    reloadThreadFiles,
  } = useCorrespondence(
    requestId,
    viewer,
    members,
    activeSpace,
    // D3: once the server has refused this person the request, the panel
    // stops asking. `reloadThreads` returns early on `enabled` false, so
    // no listing and no page is issued; the rows it still holds are hidden
    // below, never rendered.
    visible && !accessRevoked,
    onThreadRead,
    online,
    sessionOpenThread,
  );
  /**
   * #1599 Story 2.3: the conversation an arrival link named. Resolved HERE
   * because this is where the viewer's standing and the provider meet; the
   * URL was read once by the screen, which seeded the session with the id.
   */
  useArrivalThread({
    session,
    requestId,
    viewerId: viewer?.id ?? null,
    canSeeInternal,
    provider: messagingProvider,
    enabled: visible && !accessRevoked,
    accessRevoked: !!accessRevoked,
  });
  // D3: a stale load failure must not render as error text after a revoke.
  /**
   * ⚠️ AND RESET WHEN THE ROW ITSELF GOES (code review, 2026-09-18).
   * `leaveOpenThread` was the only reset, which covers every case that
   * involves LEAVING. It does not cover the one that does not: same
   * conversation, no navigation, the only file-bearing message withdrawn --
   * the block unmounts with `filesExpanded` still true, and the next file
   * anybody posts brings the row back ALREADY OPEN, shoving the conversation
   * down by up to `STRIP_MAX_HEIGHT` with no user action at all.
   *
   * Written against the row's own condition rather than a subset of it, so
   * the two cannot drift: whenever there is no row, there is no open strip.
   */
  const filesRowAbsent = !threadFiles || threadFiles.items.length === 0;
  useEffect(() => {
    if (filesRowAbsent) setFilesExpanded(false);
  }, [filesRowAbsent]);

  const error = accessRevoked ? null : loadError;

  /**
   * Errors held back since the device last went offline (#1069 offline
   * state). Set on the online -> offline edge (or at mount, offline) and
   * cleared only once the reconnect's reloads have SETTLED: a listing that
   * failed offline still holds its error for the first online render, and
   * without the hold that render flashed "Discussion error" before the reload
   * replaced it. Every error surface reads `showErrors`, never `online`.
   */
  /**
   * A reconnect is OWED: the device has been offline since the last one ran.
   * ⚠️ SESSION STATE, SET ON THE OFFLINE SIDE (review, 2026-09-26). It was a
   * per-mount ref set on the ONLINE edge, so a mount that started online after
   * a drop it never saw (a docked close across it) owed nothing, and a "Not
   * sent" line recorded at the drop, which now survives in the session, was
   * never cleared. Set whenever a mount sees the device offline, consumed by
   * the reconnect below once one is online and showing.
   */
  const reconnectPending = session.refFor('reconnectPending');
  const [offlineHold, setOfflineHold] = useState(
    () => !online || reconnectPending.current,
  );
  const showErrors = online && !offlineHold;
  /** Which hold a settling reconnect may release; bumped on every edge. */
  const holdGeneration = useRef(0);
  const wasOnline = useRef(online);
  const openThread = threads.find(t => t.id === openThreadId) ?? null;

  /**
   * A file leads back to its message (Story 7.6, D3): the pull-until-found
   * loop, over the hook's own `loadOlderMessages`. The panel wires it and
   * renders its three outputs -- `pendingMessageId` as the Files row's
   * spinner (D8), `reveal` into `MessageList` (D4), `failure` as
   * `jumpError` (D9). `useCorrespondence` gains nothing for it.
   */
  const jump = useJumpToMessage({
    openThreadId,
    messages,
    olderMessagesCursor,
    loadingMessages,
    loadingOlder,
    // The data layer's own older-page failure, told apart from a server that
    // repeats itself: the bar under the list already says "loading earlier
    // messages: ...", so the pull stops without a second line (review
    // 2026-09-19).
    olderPageFailed:
      loadError !== null && loadError.startsWith(OLDER_PAGE_ERROR_PREFIX),
    loadOlderMessages,
  });
  const { cancel: cancelJump } = jump;

  /**
   * Every file as one zip (Story 7.5, D13): the transfer, its percent and
   * its failure, as their own hook beside the jump's. The panel renders its
   * surface on the Files row (D10) and its `failure` on the line (D14);
   * `useCorrespondence` gains nothing for it.
   */
  const saveAll = useSaveAllFiles({
    openThreadId,
    requestId,
    title: openThread?.title ?? '',
    threadFiles,
  });
  const { cancel: cancelSaveAll, dismissFailure: dismissSaveFailure } = saveAll;

  /**
   * The listing can lose its last file mid-pull (the author withdrew the
   * message and the strip's refetch emptied it): the row and its spinner are
   * gone, and so is every control that could clear the line. The pull and
   * the line go with the row (review 2026-09-19).
   */
  useEffect(() => {
    if (!filesRowAbsent) return;
    cancelJump();
    setJumpError(null);
  }, [filesRowAbsent, cancelJump]);

  /**
   * Story 7.5, AC 16: a save in flight goes with the row too, but ONLY when
   * the listing has SETTLED empty (the last file-bearing message withdrawn),
   * never when it is merely `null` (review patch P7). `useCorrespondence`
   * hands back `null` on a failed refetch as well as while loading (D8), so
   * "the row is absent" also means "a background refetch just failed", and
   * a transient listing error was aborting a zip at 90%. A listing that
   * comes back finds the transfer where it left it.
   */
  const listingSettledEmpty = threadFiles
    ? threadFiles.items.length === 0
    : false;
  useEffect(() => {
    if (listingSettledEmpty) cancelSaveAll();
  }, [listingSettledEmpty, cancelSaveAll]);

  /**
   * The device's edges (#1069 offline state).
   *
   * OFFLINE: hold every error back, and take down what is open that writes or
   * saves: the delete confirmation, the rename field's claim on focus, and a
   * zip in flight (its row is hidden offline, so it could not be cancelled).
   *
   * ONLINE: fetch again, by itself; there is no Retry control. Remembered
   * rather than consumed when the panel is closed (the overlay stays mounted),
   * and run by the effect below once it is visible.
   */
  useEffect(() => {
    const was = wasOnline.current;
    wasOnline.current = online;
    if (!online) reconnectPending.current = true;
    if (was && !online) {
      holdGeneration.current += 1;
      setOfflineHold(true);
      setConfirmDelete(null);
      setRenameFocus(false);
      cancelSaveAll();
    }
  }, [online, cancelSaveAll, reconnectPending]);

  /**
   * The reconnect itself. ⚠️ EVERY CALL, AND WHY EACH:
   * - `reloadThreads`: the listing; its START also takes a stale error down.
   * - `refreshOpenThread`: the open conversation's newest page (the messages
   *   query is `retry: false`, so a page that failed offline stays failed).
   * - `reloadThreadFiles`: the open conversation's Files row. A conversation
   *   entered offline failed that listing, and `refreshOpenThread` only
   *   invalidates it when the newest page carries a file.
   * - `reloadTeam`: a team load that failed offline is a red bar otherwise.
   *
   * The lines recorded offline, or by something in flight at the drop, are
   * cleared: they describe the connection, not the reader's work. The draft
   * and staged files stay, which is the recovery. The hold lifts when every
   * reload has settled, so what shows next is what the reload found.
   */
  useEffect(() => {
    if (!online || !visible || accessRevoked) return;
    if (!reconnectPending.current) return;
    reconnectPending.current = false;
    holdGeneration.current += 1;
    const generation = holdGeneration.current;
    setSendError(null);
    setJumpError(null);
    setRenameFailed(false);
    dismissSaveFailure();
    void Promise.allSettled([
      reloadThreads(),
      reloadTeam(),
      refreshOpenThread(),
      reloadThreadFiles(),
    ]).then(() => {
      if (holdGeneration.current === generation) setOfflineHold(false);
    });
  }, [
    online,
    visible,
    accessRevoked,
    reloadThreads,
    reloadTeam,
    refreshOpenThread,
    reloadThreadFiles,
    dismissSaveFailure,
    setSendError,
    reconnectPending,
  ]);

  /**
   * Story 7.3, D8: the rename's line and editor belong to ONE conversation.
   * `leaveOpenThread` clears both on Back and on a tab switch; this covers
   * opening another conversation directly from the list, which does not go
   * through it. Story 7.6: a pull in flight and its line belong to that
   * conversation too (D7), and are reset beside the rename's.
   */
  useEffect(() => {
    setRenameDraft(null);
    setRenameFailed(false);
    setRenameCommitting(false);
    cancelJump();
    setJumpError(null);
    // Story 7.5, AC 16: and a zip in flight belonged to that conversation.
    cancelSaveAll();
  }, [openThreadId, cancelJump, cancelSaveAll]);

  /**
   * ⚠️ WHICH CONVERSATION IS OPEN *NOW*, read on the far side of the rename's
   * `await` (review patch P3). `commitRename` suspends across a network call;
   * by the time it resumes, `leaveOpenThread` or the effect above may have
   * moved the panel to another conversation and already cleared the rename
   * state. Applying the resolved result unconditionally would print thread A's
   * refusal under thread B's title. A ref, not the closed-over value, because
   * the closure captured the thread that STARTED the commit -- which is what
   * `threadId` is for -- and this must be the current one.
   */
  const openThreadIdRef = useRef(openThreadId);
  openThreadIdRef.current = openThreadId;
  /**
   * The docked panel IS unmounted when the dock collapses, so a commit can
   * resolve after this component is gone. `setState` after unmount is a warning
   * at best and a leak at worst.
   */
  const renameMountedRef = useRef(true);
  useEffect(() => {
    renameMountedRef.current = true;
    return () => {
      renameMountedRef.current = false;
    };
  }, []);

  /**
   * This thread's staging strip, and the only way to change it. Both live here
   * rather than beside the state above because `openThreadId` is the hook's,
   * and the hook is destructured above this line.
   */
  /**
   * Where the composer's files are KEYED (#1530). A reply's files, strip and
   * inline alike, live under the thread, as they did; an edit's inline images
   * under the message being edited, so they can never be posted as a reply
   * (and the strip the reply had is kept aside, untouched, for Cancel); a new
   * conversation's under one compose key. `null` when there is no composer.
   */
  const stagingKey: string | null =
    openThreadId != null
      ? editing
        ? editStagingKey(editing.messageId)
        : openThreadId
      : composingThread
      ? COMPOSE_STAGING_KEY
      : null;
  const stagedFiles =
    (stagingKey != null ? stagedByThread.get(stagingKey) : undefined) ??
    EMPTY_STAGED;
  const stagingKeyRef = useRef(stagingKey);
  stagingKeyRef.current = stagingKey;

  /**
   * Replace ONE NAMED THREAD's strip, dropping the key once it empties.
   *
   * ⚠️ THE THREAD IS AN ARGUMENT, NOT A LOOKUP, AND THAT IS THE WHOLE FIX
   * (code review run 2, 2026-09-13). This read `openThreadIdRef.current` inside
   * the updater — that is, resolved the key when React got round to running it,
   * not when the user acted. Back is not gated on `submitting`, so pressing it
   * mid-upload made the key `null` by the time the post succeeded: the updater
   * bailed, the files the server ALREADY HAD stayed staged, and pressing Post
   * again sent them a second time. Switching threads mid-upload was worse — the
   * success wiped the OTHER thread's strip. The global `setStagedFiles([])`
   * this replaced could do neither; keying state without keying the writes to
   * it is how that regression got in.
   *
   * Every caller now names the thread the action belonged to.
   */
  const setStagedForThread = useCallback(
    (
      threadId: string,
      next: (current: readonly StagedFile[]) => StagedFile[],
    ) => {
      setMayAlreadyBeSent(current => (current === threadId ? null : current));
      setStagedByThread(all => {
        const files = next(all.get(threadId) ?? EMPTY_STAGED);
        if (files.length === 0) {
          if (!all.has(threadId)) return all;
          const emptied = new Map(all);
          emptied.delete(threadId);
          return emptied;
        }
        // ⚠️ DELETE THEN SET, so this thread lands LAST and is the most
        // recently touched (code review run 3). `capStagedThreads` reads that
        // order and never evicts the final entry, which is the strip the user
        // is looking at.
        const touched = new Map(all);
        touched.delete(threadId);
        touched.set(threadId, files);
        return capStagedThreads(touched);
      });
    },
    // Session setters, stable for the session's life; named because they no
    // longer come from a local `useState` the linter can see through.
    [setMayAlreadyBeSent, setStagedByThread],
  );

  /** The open conversation's messages, for the resolver's held-file count. */
  const messagesRef = useRef(messages);
  messagesRef.current = messages;

  /** Forget one staging key whole (#1530): an edit or a compose that ended. */
  const dropStagingKey = useCallback(
    (key: string) => {
      setStagedByThread(all => {
        if (!all.has(key)) return all;
        const next = new Map(all);
        next.delete(key);
        return next;
      });
    },
    [setStagedByThread],
  );

  /**
   * The composer's text changed (#1530): keep the draft, and reconcile the
   * inline images against it. An image the editor has shown and then dropped
   * is pruned here, so its bytes are never counted or sent again; one the
   * editor has not yet reported is kept (the native bridge inserts
   * asynchronously). `reconcileInlineStaged` returns the same array when
   * nothing changed, and that is the no-write path.
   */
  const onChangeBody = useCallback(
    (html: string) => {
      setDraft(html);
      const key = stagingKeyRef.current;
      if (key == null) return;
      const current = session.get().stagedByThread.get(key);
      if (!current || !current.some(f => f.inline)) return;
      const next = reconcileInlineStaged(html, current);
      if (next === current) return;
      setStagedForThread(key, () => [...next]);
    },
    [session, setDraft, setStagedForThread],
  );

  /**
   * The editor's image resolver (#1530): the bytes become a `StagedFile`
   * marked `inline`, admitted through the SAME `admitFiles` as the paperclip
   * over the strip's files, the inline ones already staged and, for an edit,
   * the files the message already holds (the ceilings are the message's, not
   * the call's). A refusal uses the ceilings' own copy, on the unsent line
   * AND back to the editor, which shows it beside the toolbar. The in-flight
   * count is held from the first byte to the editor's insert so Send waits.
   */
  const resolveInlineImage = useCallback(
    async (file: InlineImageFile): Promise<InlineImageResolution> => {
      const key = stagingKeyRef.current;
      if (key == null) return { ok: false, error: NO_COMPOSER_FOR_IMAGE };
      beginInlineImageAttempt(COMPOSER_INLINE_IMAGE_FIELD_KEY);
      try {
        const staged = await stageInlineImageFile(file);
        // ⚠️ THE KEY IS RE-READ AFTER THE AWAIT (#1530 review, C4). Reading
        // the bytes takes time, and the composer can change hands inside it:
        // a thread switch, an edit begun or cancelled, a compose discarded.
        // Staging under the key captured BEFORE the await would park the
        // picture under a composer the user has left, counted toward that
        // thread's ceiling and sent with its next message. The editor that
        // asked is gone with the key, so there is nothing to insert into.
        if (stagingKeyRef.current !== key) {
          return { ok: false, error: NO_COMPOSER_FOR_IMAGE };
        }
        const inline: StagedFile = { ...staged, inline: true };
        const state = session.get();
        const alreadyStaged = state.stagedByThread.get(key) ?? EMPTY_STAGED;
        const held =
          state.editing != null
            ? (
                messagesRef.current.find(m => m.id === state.editing?.messageId)
                  ?.attachments ?? []
              ).map(stagedFileFromHeld)
            : [];
        const { accepted, refusals } = admitFiles(
          [...alreadyStaged, ...held],
          [inline],
        );
        if (accepted.length === 0) {
          const reason = refusals[0]?.reason ?? IMAGE_UNREADABLE;
          setSendError(reason);
          return { ok: false, error: reason };
        }
        setStagedForThread(key, current => [...current, inline]);
        return {
          ok: true,
          reference: inlineImageReference(inline.id),
          uploaded: false,
        };
      } catch (e) {
        const named = e instanceof PickerError ? e.message : '';
        return { ok: false, error: named || IMAGE_UNREADABLE };
      } finally {
        endInlineImageAttempt(COMPOSER_INLINE_IMAGE_FIELD_KEY);
      }
    },
    [session, setSendError, setStagedForThread],
  );

  /**
   * Open the platform picker and stage what fits, saying why for what does not
   * (AC 1, AC 4).
   *
   * ⚠️ THE SOURCE IS ASKED FOR, NOT ASSUMED (code review 2026-09-13). This
   * hardcoded `'files'`, which left the native photo library and camera as
   * unreachable code and AC 1 two thirds unbuilt. `chooseFileSource` returns
   * immediately on a platform with one source, so web is unchanged and there
   * is no `Platform.OS` branch here.
   *
   * ⚠️ THE REFUSALS ARE SHOWN, NOT SWALLOWED. `admitFiles` returns both halves
   * precisely so this cannot quietly truncate a multi-select, which AC 4
   * forbids in as many words. They ride the same unsent line the send failures
   * use, so there is one place in the panel that explains itself rather than
   * two competing ones.
   */
  const pickFiles = useCallback(async () => {
    // ⚠️ THE THREAD IS CAPTURED BEFORE THE DIALOG OPENS (code review run 2).
    // The picker is awaited across an OS dialog, and the panel can leave a
    // thread with no user action at all — the D3 access-revoked path does
    // exactly that. Resolving the thread afterwards meant the ceiling was
    // computed against one thread's strip and written into another's, or, when
    // the panel had closed the thread, dropped on the floor with `refusals`
    // empty so nothing was said: files chosen, nothing staged, silence.
    const threadId = openThreadId;
    if (threadId == null) return;
    try {
      const source = await chooseFileSource(pickerSources);
      // Backed out of the sheet. Not an error, and nothing staged changes.
      if (!source) return;
      // ⚠️ TWO OUTCOMES AT ONCE (code review run 2). A pick can produce files
      // AND a sentence — three photos staged, two unreadable — and both have to
      // reach the user: dropping the sentence is AC 4's silent truncation,
      // dropping the files is the all-or-nothing bug it replaced.
      const { files: chosen, error: readError } = await pickMessageFiles(
        source,
      );
      if (chosen.length === 0 && !readError) return;
      // ⚠️ THE DECISION IS MADE OUTSIDE THE UPDATER, NOT INSIDE IT. Calling
      // `setSendError` from within a state updater is a side effect in an
      // updater, which React may run more than once and warns about; it also
      // silently produced no staged files at all.
      //
      // `stagedFiles` is read from the closure, which is this thread's strip as
      // of the press — the same instant `threadId` was captured, so the ceiling
      // and the write cannot disagree about which conversation they mean.
      const { accepted, refusals } = admitFiles(stagedFiles, chosen);
      // The already-staged files are never touched by a refusal (AC 4).
      setStagedForThread(threadId, current => [...current, ...accepted]);
      // ⚠️ A CLEAN PICK LEAVES THE EXISTING LINE ALONE (code review
      // 2026-09-13). This used to be `setSendError(refusals.length ? … : null)`,
      // which wiped the AC 13 "Not sent. Your message and 3 files are still
      // here" line the user was in the middle of recovering from — attaching
      // one more file erased the only explanation of why the last attempt had
      // not gone. Refusals still speak; success is simply silent.
      // ⚠️ EACH SENTENCE ONCE (quick-dev, 2026-09-19). `admitFiles` refuses
      // per file, and a rule whose sentence does not name the file ("Up to 6
      // files per message ...", each of the two 40 MB sentences) came back
      // once PER FILE it refused: twelve images picked at once read the same
      // sentence six times. Dedupe is by sentence, so a sentence that names a
      // file ("huge.png is too large") survives once per distinct NAME; two
      // refused files that share a name read as one.
      const said = Array.from(
        new Set(
          [readError, ...refusals.map(r => r.reason)].filter((s): s is string =>
            Boolean(s),
          ),
        ),
      );
      if (said.length > 0) setSendError(said.join(' '));
    } catch (e) {
      // ⚠️ THE PICKER'S OWN SENTENCE, WHEN IT HAS ONE (code review run 2). A
      // bare `catch` here replaced `Could not read nameplate.jpg.` with a
      // generic line, so the per-file copy the pickers carefully build was
      // reachable only from its unit test.
      // ⚠️ ONLY A `PickerError`'S MESSAGE IS THE PRODUCT'S (code review run 3,
      // 2026-09-13). This was `e instanceof Error ? e.message : ''`, which
      // rendered ANY error that escaped the block: `launchImageLibrary`'s
      // platform string, an `RNFS` `ENOENT` carrying a device path, and a
      // coding bug inside the try as `undefined is not a function` — in a flow
      // whose every other refusal is audited copy. Run 2 was right that the
      // pickers' named sentences should reach the user; the mechanism took
      // everything else with them.
      const named = e instanceof PickerError ? e.message : '';
      setSendError(
        named || 'Those files could not be read. Try choosing them again.',
      );
    }
  }, [openThreadId, stagedFiles, setStagedForThread, setSendError]);

  /**
   * The gallery closes with the conversation it belongs to: a thread switch
   * (Back, another conversation) or access being revoked, which also nulls
   * the thread. The carousel unmounts and aborts its own loads; a fetch the
   * cache still has on the wire lands in the store and paints nothing.
   */
  useEffect(() => {
    setGallery(null);
  }, [openThreadId]);
  useEffect(() => {
    if (accessRevoked) setGallery(null);
  }, [accessRevoked]);
  // The overlay panel is never unmounted (C7): its close must take the
  // gallery with it, or it is still up when the panel comes back.
  useEffect(() => {
    if (!visible) setGallery(null);
  }, [visible]);
  /** The set on screen, for the loader's neighbour-size rule (read at call). */
  const galleryRef = useRef(gallery);
  galleryRef.current = gallery;

  /**
   * Story 8.3: what the gallery has shown in THIS conversation. Emptied on
   * thread switch, on the overlay's close and on revocation (the hook's three
   * effects). The carousel decides WHEN a file is asked for (current ±1); the
   * store decides whether that costs a fetch.
   */
  const fileCache = useMessageFileCache({
    openThreadId,
    visible,
    accessRevoked,
  });

  const openAttachment = useCallback(
    (message: Message, index: number, kind: 'image' | 'document') => {
      const all = message.attachments ?? [];
      // Each item is stamped with THIS message's id (Story 7.4, D4).
      const openable: OpenableFile[] = all.map(f => ({
        ...f,
        messageId: message.id,
      }));
      //
      // ⚠️ `partitionForDisplay`, NOT `kind === 'image'` — AND THAT IS A BUG
      // FIX, FOUND BY EXTRACTING THE PREDICATE IN STORY 7.4 (D3).
      //
      // `MessageAttachments` reports an index into the list it DREW, and it
      // draws by "images we can draw": an image with no `thumbnailUri` (an
      // iPhone HEIC — `sharp` cannot decode it, so no thumbnail is stored) is
      // rendered as a CHIP, in the document group. Partitioning by `kind`
      // alone made the two lists disagree the moment such a file was present.
      // Sharing the predicate is what makes them agree by construction.
      //
      // ⚠️ ONE SET PER MESSAGE, PICTURES THEN DOCUMENTS (owner ruling
      // 2026-10-06, GitLab #1539). "Images page, documents open alone"
      // (2026-09-14) existed because a mixed set could step from a `.pptx`
      // onto a `.pdf`, which routed to a DIFFERENT modal, and two ended up
      // mounted at once. One modal with a renderer per kind removes the
      // reason, so the bubble's display order IS the gallery order.
      const { pictures, documents } = partitionForDisplay(openable);
      const ordered = [...pictures, ...documents];
      // A negative index would land on the last picture through the
      // document arithmetic below.
      if (index < 0) return;
      const initialIndex = kind === 'image' ? index : pictures.length + index;
      if (!ordered[initialIndex]) return;
      setGallery({ items: ordered.map(toGalleryItem), initialIndex });
    },
    [],
  );

  /** Dismiss the gallery; the carousel's own loads abort with its unmount. */
  const closeGallery = useCallback(() => {
    setGallery(null);
  }, []);

  /**
   * An item in the strip: a JUMP to the message it was posted with (#1069
   * Story 7.6, D2; owner, 2026-09-19: "the strip is an index, not a viewer").
   * Tile and chip alike, no split by file type. Nothing in the strip opens a
   * file any more -- `openStripImage` and `openStripDocument` are gone, and
   * the gallery has exactly one opener, the bubble's `openAttachment` (C1;
   * the panel suite counts them). The file is
   * one tap away in the bubble the reader lands on (FR16), which is the
   * asymmetry that decided it: jump first and both are kept.
   *
   * The item is handed over rather than an index (the strip's D2): the jump
   * needs `messageId`, which every `ThreadFile` carries (7.4 AC 1). The hook
   * reveals at once when the message is loaded and pulls older pages until it
   * lands otherwise; the effects below turn its outputs into the panel's.
   */
  const { request: requestJump } = jump;
  const selectStripFile = useCallback(
    (file: ThreadFile) => {
      setJumpError(null);
      requestJump(file.messageId);
    },
    [requestJump],
  );

  // The message was found (D8): the strip closes BEFORE the scroll starts, so
  // the list is not moving under a drawer that is about to go.
  useEffect(() => {
    if (jump.reveal === null) return;
    animateFilesDrawer();
    setFilesExpanded(false);
  }, [jump.reveal, animateFilesDrawer]);

  // Unreachable (D9): said on the viewer's line; the strip stays open.
  useEffect(() => {
    if (jump.failure === 'unreachable') setJumpError(JUMP_UNREACHABLE);
  }, [jump.failure]);

  /**
   * ⚠️ IT TAKES THE FILE IT IS HANDED (Story 7.4, D4). It used to take a
   * `Message` and a file id and look the file up in `message.attachments`,
   * which cannot work for the files strip: the strip's items are not the
   * attachments of any one message, so the lookup would fail and the download
   * would silently do nothing. The one caller that HAS a message
   * (`MessageList`'s own Download) does the lookup at the call site, where the
   * message is.
   */
  const downloadAttachmentFile = useCallback(
    async (messageId: string, file: MessageFile, retry = 0): Promise<void> => {
      if (openThreadId == null) return;

      // ⚠️ THE BEHAVIOURS `AttachmentActions.handleDownload` ALREADY HAS,
      // matched rather than approximated (2026-09-14): the Android toast and
      // the retry below.
      //
      // ⚠️ OFFLINE IS A SILENT RETURN (#1069 offline state, 2026-09-25). It
      // used to print "Download is unavailable while offline" on web and
      // raise a native `Alert` -- saying offline through a failure. Every
      // control that reaches this is hidden while the device is offline, so
      // the guard is defensive: it keeps a raw fetch error off the screen if
      // one ever slips through, and says nothing.
      if (!networkService.isConnected()) return;

      try {
        // Story 8.3, D5: a Save of the photo on screen does not download it
        // again, and a Save while its bytes are on the wire (a step joined
        // the neighbour's prefetch) waits on that fetch rather than starting
        // a second. A miss is fetched and NOT inserted: the budget is the
        // viewer's, and a file downloaded from a chip was never shown and
        // will not be stepped to.
        const fetched =
          fileCache.peek(file.id) ??
          (await (fileCache.join(file.id) ??
            fetchMessageFile({
              threadId: String(openThreadId),
              messageId,
              fileId: file.id,
            })));
        await saveBase64File(fetched.base64, file.name, fetched.mimeType);
        if (Platform.OS === 'android') {
          // Web shows its own download UI and iOS shows the share sheet;
          // Android is the one platform with no confirmation of its own.
          showToast.success('File saved to Downloads');
        }
      } catch (e) {
        // A failure that lands while the device is offline is the connection,
        // not the file: no 'Download Failed … Retry' over a network that is
        // not there, and no line (#1069 offline state).
        if (!networkService.isConnected()) return;
        const reason =
          e instanceof Error && e.message
            ? e.message
            : 'Failed to download attachment';
        if (retry < MAX_DOWNLOAD_RETRIES) {
          const again = await showConfirmation(
            'Download Failed',
            reason,
            'Retry',
            'Cancel',
          );
          if (again) {
            await downloadAttachmentFile(messageId, file, retry + 1);
            return;
          }
        }
        setSendError(reason);
      }
    },
    [openThreadId, fileCache, setSendError],
  );

  /**
   * The strip's per-item save (Story 7.5, D2, C2): the SAME function the
   * bubble's Download uses, handed the file the strip already holds --
   * `ThreadFile` carries `messageId` (7.4 D4), so no lookup. Offline copy,
   * Android toast, retry dialog and the line are all inherited.
   */
  const saveStripFile = useCallback(
    (file: ThreadFile) => {
      void downloadAttachmentFile(file.messageId, file);
    },
    [downloadAttachmentFile],
  );

  const removeStagedFile = useCallback(
    (id: string) => {
      // Removing is a press on a strip that is on screen, so the open thread
      // IS the thread — but it is named rather than looked up, like every
      // other writer, so no future edit has to rediscover why.
      if (openThreadId == null) return;
      setStagedForThread(openThreadId, current =>
        current.filter(f => f.id !== id),
      );
    },
    [openThreadId, setStagedForThread],
  );

  /**
   * D3: leave the conversation the moment access is gone. The list region
   * renders nothing either way; this is what removes the Back control and
   * the title, and what the guards in the hook key their "still open" on.
   *
   * ⚠️ AND THE WHOLE PLACE GOES WITH IT (review, 2026-09-26). The session
   * outlives a close, so resetting only the open conversation left an edit,
   * its displaced draft, a compose and the lines about them to resume if
   * access came back: an edit of a message from the revoked view, now saving
   * inside whatever conversation was opened next.
   */
  useEffect(() => {
    if (!accessRevoked) return;
    resetSessionAfterRevoke(session);
    // Story 7.5, AC 16: a zip in flight is called off with the access. The
    // thread-switch effect would catch it a render later; this is the pin.
    cancelSaveAll();
  }, [accessRevoked, session, cancelSaveAll]);

  /**
   * The tab badges, per space (#1069 P3.2, rewritten by P3.3).
   *
   * ⚠️ THIS USED TO BE `summary || local` AND THAT OR WAS A BUG THE USER HIT.
   * Reported verbatim: "when i read all internal messages the dot in the
   * internal tab was still visible it should dissapear". Reading every thread
   * drives the local half `false`; the summary half is still `true` from the
   * last fetch; `false || true` is `true`. **No local read could ever clear
   * it.** The union was defended as "early but never wrong" — it is wrong in
   * precisely one direction, and that direction is a badge that will not go
   * away, which is the single most damaging thing this feature can do because
   * it teaches people to ignore the badge.
   *
   * ⚠️ SO FOR THE ACTIVE SPACE THE LOCAL ANSWER IS AUTHORITATIVE IN BOTH
   * DIRECTIONS, and it is the better answer on both counts:
   *
   *   · it compares the VENDOR's `lastPostedAt`, while the summary compares our
   *     `last_activity_at` mirror, which a message typed into Flarum's own web
   *     UI never moves;
   *   · it is the only half that can see a mark-read the user just performed.
   *
   * It also closes a divergence found while diagnosing the report:
   * `listThreads` drops index rows whose Flarum discussion fails to hydrate,
   * while the server summary still counts them — so a space can be entirely
   * read in this panel and summarise as unread FOR EVER. A refetch cannot fix
   * that. Believing the rows the user can actually see can, and does.
   *
   * ⚠️ AND IT IS AUTHORITATIVE ONLY WHEN WE ACTUALLY HOLD IT — otherwise this
   * fix would trade a badge that never clears for one that never appears:
   *
   *   · `threadsKey` matching this request AND this space: `reloadThreads`
   *     does NOT clear `threads` while the next load runs, so the array can
   *     still describe the space the user LEFT or, after a navigation between
   *     requests, another request entirely.
   *     ⚠️ HONEST NOTE ON THIS ONE: the hook also nulls the key the moment a
   *     load starts and on failure, and THAT is what the tests reach. Mutating
   *     this line to `threadsKey !== null` leaves every test green, because the
   *     only state it distinguishes is the single render between a prop change
   *     and the effect that reacts to it — which no query can observe, since
   *     React flushes both inside one `act`. It is kept because that render is
   *     real and the comparison costs nothing, not because it is proven.
   *   · every row carrying a computed `unread`: the server returns `undefined`
   *     for all of them or none (its read-state lookup failed for the listing;
   *     since Story 2.1 that is the only way a row is left uncomputed, an
   *     oversight reader's being computed like everybody else's). All-
   *     `undefined` is "not computed", never "nothing unread".
   *
   * An empty list for the active space IS a local answer — no threads, nothing
   * unread — so `every` over `[]` being `true` is wanted, not an accident.
   */
  const activeSpaceAnswerIsLocal =
    !accessRevoked &&
    threadsKey === threadsKeyFor(requestId, activeSpace) &&
    threads.every(t => t.unread !== undefined);
  const activeSpaceHasUnread = threads.some(t => t.unread === true);

  /**
   * ⚠️ AND WHAT WE ESTABLISH LOCALLY IS REPORTED UP, BECAUSE THE PANEL IS NOT
   * THE ONLY SURFACE SHOWING IT. The launcher pill lives OUTSIDE this panel and
   * renders the summary; keeping this answer in here is what left the pill lit
   * after the user had read everything and closed the panel — the third
   * sighting of one bug (`useUnreadSpaceState.ts` names all three). The screen
   * merges what we send with the server's answer and feeds the merged value
   * back to every surface, including our own inactive tab below.
   *
   * Fired only while we actually HOLD a local answer, and `noteLocalAnswer` is
   * idempotent, so a repeated report costs one comparison and no render.
   */
  useEffect(() => {
    if (activeSpaceAnswerIsLocal) {
      onSpaceUnreadKnown?.(activeSpace, activeSpaceHasUnread);
    }
  }, [
    activeSpaceAnswerIsLocal,
    activeSpaceHasUnread,
    activeSpace,
    onSpaceUnreadKnown,
  ]);

  /**
   * THE CLOCK (Story 2.3, AC 6; FR69; D4). The screen's summary ticks every
   * 10 s while this panel is open and carries, per space, when anything in
   * that audience last moved for this viewer. When the string for the space
   * ON SCREEN changes from a value already seen, re-list that space through
   * the provider, exactly as on open: the rows come back with the server's
   * per-person `unread` and their own clocks, so the row dot and the tab
   * badge light with nobody pressing anything, and an edit or a withdrawal
   * (which move activity and never unread, AR5) reach the list too, and the
   * open conversation's NEWEST PAGE (review decision 1: a change above it
   * waits for a scroll or a reopen).
   *
   * ⚠️ THIS REPLACES THE "RISE" EFFECT that re-asked when the summary's
   * `hasUnread` went false→true while our rows said otherwise. A rise cannot
   * happen without the clock moving (a new message touches both), so the
   * clock is a strict superset that also covers what the rise never could.
   *
   * ⚠️ NEVER ON A FIRST OBSERVATION, A REOPEN OR A TAB CHANGE. The listing
   * is already fresh on all three (`reloadThreads` fires on `enabled` and on
   * `space`), so the clock is SEEDED there and compared only afterwards. The
   * overlay panel is never unmounted (its `visible` flips), so "reopen" is a
   * real edge here and the docked panel, which IS unmounted, comes through
   * the same seed on mount.
   *
   * ⚠️ PRIMITIVES IN THE DEPENDENCY LIST, NEVER THE `spaceActivity` OBJECT
   * (C5). The screen memoises it, but this effect must not rely on that: an
   * equal-content object built fresh each render would run this — and its
   * `reloadThreads` — in a loop, which this codebase has paid for once at
   * ~2,600 requests in seconds. The string for the active space is the key.
   */
  const activeSpaceActivity =
    activeSpace === 'internal'
      ? spaceActivity?.internal
      : spaceActivity?.shared;
  /** The last clock seen per space; `undefined` until a value has been seen. */
  const observedInternalClock = useRef<string | null | undefined>(undefined);
  const observedSharedClock = useRef<string | null | undefined>(undefined);
  const observedClockFor = (s: SpaceId) =>
    s === 'internal' ? observedInternalClock : observedSharedClock;
  const wasVisible = useRef(false);
  const lastClockSpace = useRef<SpaceId | null>(null);
  const lastClockRequest = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (!visible) {
      wasVisible.current = false;
      return;
    }
    const reopened = !wasVisible.current;
    wasVisible.current = true;
    // A request change is a seed too (second pass, 2026-09-11): the overlay
    // panel is never unmounted, and a new request served from the query
    // cache brings its own clock without passing through `undefined`.
    const switched =
      lastClockSpace.current !== activeSpace ||
      lastClockRequest.current !== requestId;
    lastClockSpace.current = activeSpace;
    lastClockRequest.current = requestId;

    const observed = observedClockFor(activeSpace);
    const before = observed.current;
    /**
     * `null` AFTER A VALUE IS NOT A MOVE (code review 2026-09-11). The
     * server answers `empty(caller)`, clocks `null`, when its summary service
     * fails (NFR54), and an audience does not otherwise go from something to
     * nothing: no route soft-deletes a thread today (`softDelete` exists on
     * the repository and the index, unused; if a withdrawal feature ever
     * calls it, the last thread leaving a space would look like this degrade,
     * see `deferred-work.md`). So a
     * `null` that follows a value keeps the value it followed: the degrade is
     * transparent, and recovery to the SAME clock re-asks nothing. A `null`
     * that follows nothing IS recorded, so the audience's first conversation
     * (`null` → a value) counts as the move it is.
     */
    observed.current =
      activeSpaceActivity === null && typeof before === 'string'
        ? before
        : activeSpaceActivity;

    // Seed on open, reopen and tab change: the listing those fire is fresh.
    if (reopened || switched) return;
    // First observation for this space (the summary arriving after the
    // panel opened), the clock going away (a revoke masks the summary), or
    // the degrade above: nothing to compare against, nothing to re-ask.
    if (before === undefined || activeSpaceActivity == null) return;
    if (activeSpaceActivity === before) return;

    // Coalesced, never dropped, when a listing is already in flight
    // (`useCorrespondence`, `rerunRequested`): the answer on the wire was
    // asked before this move and cannot carry it.
    reloadThreads().catch(() => {
      // `reloadThreads` records its own error; a clock-driven refresh must
      // not surface one of its own.
    });
  }, [activeSpaceActivity, activeSpace, visible, requestId, reloadThreads]);

  /**
   * THE SECOND STEP (Story 2.3, AC 7; D2, D6): whenever a listing LANDS,
   * compare the OPEN conversation's own clock with the value held when it was
   * last loaded; if it moved, refetch its newest page. Never the page without
   * the listing: the listing carries the row's clock and the per-person
   * `unread`, and it is what the tab badge reads. Skipped when nothing is
   * open or the open row did not move.
   *
   * ⚠️ EVERY LANDING, NOT ONLY A CLOCK-DRIVEN ONE (code review 2026-09-11).
   * A "clock reload pending" flag used to gate this, and it stranded: set
   * before a `reloadThreads()` that coalesced behind an in-flight listing,
   * consumed by THAT listing's stale rows, and never set again for the
   * follow-up that carried the reply. With no flag there is nothing to
   * strand; a landing whose open row did not move costs one string compare,
   * and a reopen whose open row DID move (the conversation is being shown
   * again) refetches, which is right.
   *
   * The held clock is a ref assigned in an effect (as `openThreadIdRef` is
   * in the hook): committed values only. Keyed on `threadsKey`, which the
   * hook nulls when a listing starts and writes when it lands, rather than
   * on the rows array (C5).
   */
  const threadsRef = useRef(threads);
  useEffect(() => {
    threadsRef.current = threads;
  }, [threads]);
  const openThreadClockRef = useRef<string | null>(null);
  useEffect(() => {
    openThreadClockRef.current = openThreadId
      ? threadsRef.current.find(t => t.id === openThreadId)?.lastActivityAt ??
        null
      : null;
  }, [openThreadId]);

  useEffect(() => {
    // Not for a panel the user has closed, or one the server has refused
    // (second pass, 2026-09-11): a listing that lands after the close must
    // not fetch a page and mark a conversation read behind the user's back.
    // `reloadThreads` is stopped by `enabled`; this step was not.
    if (!visible || accessRevoked) return;
    if (loadingThreads || threadsKey !== threadsKeyFor(requestId, activeSpace))
      return;
    if (!openThreadId) return;
    const row = threadsRef.current.find(t => t.id === openThreadId);
    if (!row) return;
    const clock = row.lastActivityAt ?? null;
    if (clock === openThreadClockRef.current) return;
    // Held BEFORE the fetch: a refresh that fails is not retried for this
    // move (a later move compares against this value and asks again).
    openThreadClockRef.current = clock;
    refreshOpenThread().catch(() => {
      // `refreshOpenThread` swallows its own failure; nothing to add.
    });
  }, [
    visible,
    accessRevoked,
    threadsKey,
    loadingThreads,
    openThreadId,
    requestId,
    activeSpace,
    refreshOpenThread,
  ]);

  /**
   * The tab badges.
   *
   * ⚠️ THE ACTIVE TAB RENDERS **OUR OWN ROWS OR NOTHING** — never the server's
   * answer, not even for the moment before the rows land. Falling back to the
   * server there is what made the tab flash "New" on reopen and then correct
   * itself: the stale value being visibly overwritten. The owner's words were
   * "it shows 'new' in tabs, but the[n] dissapears", and a badge that appears
   * and vanishes is worse than one that appears late — the list it describes is
   * loading directly underneath it either way.
   *
   * The INACTIVE tab has no rows of its own and takes the merged value, which
   * already carries whatever this panel last reported for that space. That is
   * what stops the badge relighting when the user presses the other tab.
   */
  const unreadForSpace = (s: SpaceId, merged?: boolean): boolean =>
    accessRevoked
      ? false
      : s === activeSpace
      ? activeSpaceAnswerIsLocal && activeSpaceHasUnread
      : merged ?? false;
  const tabUnread: Record<SpaceId, boolean> = {
    internal: unreadForSpace('internal', spaceUnread?.internal),
    shared: unreadForSpace('shared', spaceUnread?.shared),
  };

  // Plus whoever has spoken in the OPEN conversation without being on the
  // team (2026-10-06): `messages` is the open thread's page, empty when none
  // is open, and the thread is in `activeSpace` by construction, so the fold
  // sits after the space filter.
  const spaceMembers = useMemo(
    () => withNonMemberAuthors(membersForSpace(members, activeSpace), messages),
    [members, activeSpace, messages],
  );

  /**
   * Reply, or Save when editing (Story 1.3, D2). `openThreadId`, never
   * `openThread` — the latter is a lookup into a list that is transiently
   * missing a just-created thread.
   *
   * The draft is cleared only AFTER the provider confirms. Clearing first was
   * survivable while the composer held a short title; it is data loss the
   * moment it holds a pasted email. A refused post (403, 400) shows the
   * server's explanation in the unsent line and keeps the draft (AC 37).
   */
  const postDraft = async (draft: string) => {
    /**
     * #1530: the inline images the HTML still refers to go with it, after the
     * strip's files, and the HTML's staged refs become their indexes. An
     * inline file the editor dropped is neither sent nor counted.
     */
    const outgoing = outgoingMessage(wireDraft(draft), stagedFiles);
    const body = outgoing.html;
    // ⚠️ FILES ALONE ARE A MESSAGE (#1069 Story 4.1, AC 6, C4, FR2). The old
    // guard was `if (!body)`, which would have refused a photo with no caption
    // before it reached the server. An EDIT may carry files too since #1530
    // (inline images added in the box), and the server allows an EMPTY
    // caption on an edit when the message holds or gains files (its C4), so
    // the gate below is the server's: text, or files going, or files held.
    const carriesFiles = outgoing.files.length > 0;
    const editedHoldsFiles =
      editing != null &&
      (messagesRef.current.find(m => m.id === editing.messageId)?.attachments
        ?.length ?? 0) > 0;
    // ⚠️ CAPTURED EXPLICITLY, NOT RELIED ON (code review 2026-09-13, corrected
    // at run 2). AC 13's line names the files that WENT. `post` is a fresh
    // closure per render, so reading `stagedFiles.length` in the failure branch
    // already gave the press-time count — the original finding claimed the
    // rendered count was live and that was WRONG, which the story records.
    // This local is a clarification, not a fix: it makes the capture a stated
    // fact rather than a property of closures that a later refactor could lose.
    const sentFileCount = outgoing.files.length;
    /**
     * ⚠️ WHAT WAS SENT, CAPTURED FOR A COMPLETION THAT MAY RUN IN ANOTHER
     * MOUNT (#1069, 2026-09-25). The draft, the strip and the edit are session
     * state, so a send outlives the panel that started it: a resize or a
     * docked close mid-send remounts the panel while this closure keeps
     * running, and its writes land on the session the NEW mount is showing.
     * So the completion clears only what it sent: the draft if it is still
     * exactly this text, the strip's files by identity, the edit if it is
     * still this one. Anything typed, staged or started after the press
     * belongs to the next message and is left alone.
     */
    const draftAtPress = draft;
    const sentFiles: readonly StagedFile[] = editing ? [] : outgoing.files;
    const editAtPress = editing;
    const editKeyAtPress = editing ? editStagingKey(editing.messageId) : null;
    // The strip the caution belongs to: the edit's own key while editing, the
    // thread's otherwise (the same key `stagedFiles` is read under).
    const stagingKeyAtPress = editKeyAtPress ?? openThreadId;
    if (
      (messageTextLength(body) === 0 && !carriesFiles && !editedHoldsFiles) ||
      !openThreadId ||
      submittingRef.current
    ) {
      return;
    }
    submittingRef.current = true;
    setSubmitting(true);
    setSendError(null);
    try {
      const outcome = editing
        ? await editMessage(
            openThreadId,
            editing.messageId,
            body,
            toOutgoingFiles(outgoing.files),
          )
        : await postMessage(
            openThreadId,
            body,
            toOutgoingFiles(outgoing.files),
          );
      /**
       * ⚠️ A CANCEL IS NOT A FAILURE, AND THIS IS WHERE THAT WAS LOST (code
       * review 2026-09-13). The hook has always distinguished the two, but
       * both arrived here as `false`, so pressing Cancel printed AC 13's
       * unsent line — "Not sent. Your message and 3 files are still here" — at
       * someone who had just asked for exactly that. AC 11 says the message
       * and its files come back and NOTHING is said; they are back, because
       * the composer never gave them up, so there is nothing left to say.
       */
      if (outcome === 'cancelled') {
        // ⚠️ A CANCEL THAT CARRIED FILES IS NOT KNOWN TO HAVE BEEN UNDONE
        // (owner decision, code review run 3, 2026-09-13). `xhr.abort()` tears
        // down the connection, not the write, so a cancel at 99% can land after
        // the server committed — and AC 12 keeps the strip, so the committed
        // message shows in the thread while its exact files sit in the composer
        // looking unsent. One press of Post sent them twice. AC 11's silence is
        // intact: the unsent LINE is still not shown, because nothing failed;
        // the caution goes on the strip, next to the files it is about.
        if (carriesFiles) setMayAlreadyBeSent(openThreadId);
        return;
      }
      if (outcome === 'unconfirmed') {
        // Story 6.7, D4: the body left, nothing came back. Three things on
        // screen, each answering a different question: this line (what
        // happened to the message), the strip caution (what to do about the
        // files), and the Discussion error banner from `error` (the
        // transport's sentence). `sentFileCount` is the press-time count,
        // never the strip's live one.
        // #1530 review, C1: an edit that added files takes the same path;
        // the caution lands on the edit's own strip key.
        setSendError(unconfirmedPost(sentFileCount));
        if (carriesFiles) setMayAlreadyBeSent(stagingKeyAtPress);
        return;
      }
      if (outcome === 'failed') {
        setSendError(
          editing
            ? UNSENT_SAVE
            : carriesFiles
            ? unsentPostWithFiles(sentFileCount)
            : UNSENT_POST,
        );
        return;
      }
      // What it sent and only that (`settleSentPost`): the draft if it is
      // still this text, the strip's files by identity, the edit if it is
      // still this one.
      //
      // ⚠️ CLEARED ONLY ON SUCCESS, exactly like the draft and for the same
      // reason (AC 12, AC 13): a failed or cancelled post must hand the files
      // back, and the handles cannot be re-derived once dropped.
      //
      // ⚠️ AND ONLY WHEN THE SUCCESS WAS A POST (code review 2026-09-13). This
      // ran on every successful write, edit included: attach two photos to a
      // reply, tap Edit on an earlier message, fix a typo, save — and both
      // photos vanished, unsent and unmentioned. The comment above reasoned
      // about a failed post and never considered that the success in question
      // might be a different operation.
      //
      // ⚠️ AND AGAINST THE THREAD IT WAS POSTED TO (code review run 2). `Back`
      // is not gated on `submitting`, so the open thread can be gone or
      // different by the time this runs. `openThreadId` here is the one
      // captured by the guard at the top of `post`, which is the thread the
      // files were sent to.
      settleSentPost(session, {
        threadId: openThreadId,
        draftAtPress,
        sentFiles,
        editAtPress,
      });
      // #1530: an edit's inline images went with it; the key goes too.
      if (editKeyAtPress) dropStagingKey(editKeyAtPress);
      // Sent. The composer's height goes back to the default with the text,
      // for the same reason the text goes: this message is finished.
      noteSendSucceeded();
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };
  /**
   * The composer's Post: the draft on screen. `postDraft` takes the draft as
   * an argument (#1599 Story 1.4) because "Remove @Name and send" posts the
   * STRIPPED draft in the same press, before this render's `draft` could
   * hold it.
   */
  const post = () => postDraft(draft);

  /**
   * New conversation: ONE call carrying the title AND the first message
   * (Story 1.3, D1). The server creates both rows in one transaction, so there
   * is no partial failure to land the user in: either the conversation exists
   * with its message, or nothing was created and both fields keep their text.
   */
  const createThreadFrom = async (draft: string) => {
    const title = newTitle.trim();
    // #1530: the first message's inline images ride the same call (C3).
    const outgoing = outgoingMessage(wireDraft(draft), stagedFiles);
    const body = outgoing.html;
    if (!title || messageTextLength(body) === 0 || submittingRef.current)
      return;
    const sentFileCount = outgoing.files.length;
    // The EFFECTIVE space at submit, compared below with the effective space
    // at landing, both read from the session: like with like, so an external
    // viewer (whose `space` can say 'internal' while they are in 'shared') is
    // never told the send went elsewhere, and a standing change mid-create is
    // seen as the move it is.
    const spaceAtSubmit = activeSpaceOf(session.get());
    // Captured for a completion that may run in another mount; see `post`.
    const draftAtPress = draft;
    const titleAtPress = newTitle;
    /**
     * Leaves compose mode only if the title and body still hold what was
     * sent (`settleSentCreate`): after a resize mid-create they are the NEW
     * mount's, and the user may have moved on in it.
     */
    const clearSentCompose = () => {
      settleSentCreate(session, { titleAtPress, draftAtPress });
      // #1530: the first message's inline images went with it.
      dropStagingKey(COMPOSE_STAGING_KEY);
      bumpBodySeed();
    };
    submittingRef.current = true;
    setSubmitting(true);
    setSendError(null);
    try {
      const created = await createThreadOnProvider(
        title,
        body,
        toOutgoingFiles(outgoing.files),
      );
      // #1530 review, C1: the first message's files left the device and the
      // server never answered, so the conversation MAY exist. The hook has
      // reloaded the listing; both fields keep their content (the compose
      // form stays), the line is the cautious one, and the caution sits by
      // the images under the compose key, exactly as a reply's does.
      if (created.outcome === 'unconfirmed') {
        setSendError(unconfirmedPost(sentFileCount));
        if (sentFileCount > 0) setMayAlreadyBeSent(COMPOSE_STAGING_KEY);
        return;
      }
      // Creation refused: BOTH fields keep their content and the hook has
      // already surfaced the error (and the server's explanation, when it
      // gave one). Nothing was created, so nothing to undo.
      if (created.outcome !== 'sent') {
        setSendError(UNSENT_SEND);
        return;
      }
      const id = created.id;
      /**
       * The user changed tab while this was in flight. The conversation and
       * its message were SENT, in the space at submit, so nothing is lost; but
       * opening it here would put it under the other space's notice, so we do
       * not. Both fields clear (the message is delivered), the line says
       * where it went, and the listing the user is looking at reloads.
       */
      //
      // Why this branch exists: the thread view is keyed on `openThreadId`,
      // which gave up the old `threads.find()` invariant that you could only
      // be inside a thread from the active space's list. Opening this thread
      // here would put a shared conversation under "Internal only. The
      // customer cannot see this.", and every reply typed there would go to
      // the customer: the confidentiality inversion the spec forbids.
      //
      // ⚠️ THE SESSION'S EFFECTIVE SPACE, NOT A PER-MOUNT REF (2026-09-25,
      // corrected at review 2026-09-26). It was an `activeSpaceRef` owned by
      // the mount that pressed Send; after a resize that mount is gone and
      // its ref is frozen, so a tab press in the NEW mount went unseen and the
      // inversion came back. And not the raw `space` either: the viewer's
      // standing moves the effective space without a tab press.
      if (activeSpaceOf(session.get()) !== spaceAtSubmit) {
        // Leave compose mode too (review): the message is delivered, and an
        // empty compose card under the "sent in the other tab" line was the
        // one thing this branch forgot that the same-tab branch does.
        clearSentCompose();
        setSendError(SENT_IN_PREVIOUS_TAB);
        noteSendSucceeded();
        reloadThreads().catch(() => {
          // `reloadThreads` records its own error.
        });
        return;
      }
      setOpenThreadId(id);
      clearSentCompose();
      noteSendSucceeded();
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  /** The composer's Create: the draft on screen (see `post`). */
  const createThread = () => createThreadFrom(draft);

  /**
   * "Remove @Name and send" (#1599 Story 1.4, AC 9): strip exactly the tokens
   * the server refused from the draft, remount the editor on the stripped
   * text (it is seeded once from `body`, so without the seed it would keep
   * showing them), clear the refusal and post again through the same path.
   * A token the server did not refuse is untouched; the files stay staged.
   */
  const removeRefusedMentionsAndSend = () => {
    const stripped = stripMentionTokens(draft, refusedMentionIds);
    onChangeBody(stripped);
    bumpBodySeed();
    setRefusedMentionIds([]);
    setSendError(null);
    void (openThreadId != null
      ? postDraft(stripped)
      : createThreadFrom(stripped));
  };

  const composerMode: ComposerMode =
    openThreadId != null
      ? editing
        ? 'edit'
        : 'reply'
      : composingThread
      ? 'compose'
      : 'idle';

  /**
   * #1599 Story 1.4 (AC 7, AC 8): who may be mentioned in the space on
   * screen. The panel owns this, never the composer (which knows no request,
   * thread or space id; story correction 3). Fetched while the composer is
   * open, once per space (the key carries `activeSpace`, so a tab press
   * fetches the other list) and never per keystroke. With the runtime flag
   * off nothing is fetched and the editor gets a disabled source.
   */
  const runtimeFlags = useRuntimeFeatureFlags();
  const mentionsOn = runtimeFlags.correspondenceMentions;
  const mentionable = useQuery(
    mentionableQuery({
      provider: messagingProvider,
      requestId,
      space: activeSpace,
      viewerId: viewer?.id ?? null,
      enabled:
        mentionsOn &&
        composerMode !== 'idle' &&
        visible &&
        online &&
        !accessRevoked,
    }),
  );
  const mentionableData = mentionable.data;
  const mentionableFailed = mentionable.isError;
  // A query that is pending but not fetching is DISABLED (offline, an
  // offline-only request, revoked): it would read "loading" forever, so the
  // suggester is switched off instead (review 2026-10-22).
  const mentionablePending = mentionable.isPending;
  const mentionableIdle =
    mentionablePending && mentionable.fetchStatus === 'idle';
  const mentionSuggest = useMemo<MentionSuggestSource | undefined>(
    () =>
      mentionsOn
        ? {
            enabled: !mentionableData?.disabled && !mentionableIdle,
            status: mentionableFailed
              ? 'failed'
              : mentionablePending
              ? 'loading'
              : 'ready',
            candidates: toMentionCandidates(mentionableData),
            refusedIds: refusedMentionIds,
          }
        : undefined,
    [
      mentionsOn,
      mentionableData,
      mentionableFailed,
      mentionablePending,
      mentionableIdle,
      refusedMentionIds,
    ],
  );

  /** Whether the edit in the box differs from what it was prefilled with. */
  const editDirty = editing !== null && draft !== editPrefill.current;

  const startEdit = (message: Message, priorDraft: string) => {
    draftBeforeEdit.current = priorDraft;
    /**
     * Prefilled with the INVERSE of the store, never the text projection
     * (review, promoted): `messageBodyText` dropped the tags, so saving an
     * untouched edit re-sanitized the projection and destroyed the markup.
     */
    const prefill = messageHtmlForEditor(message.body, message.attachments);
    editPrefill.current = prefill;
    setDraft(prefill);
    setEditing({ messageId: message.id });
    setSendError(null);
    // A refusal belongs to the write it answered (#1599 Story 1.8 review):
    // carried into an edit, its ids would offer "Remove @Name and save" for a
    // save the server never refused.
    setRefusedMentionIds([]);
    bumpBodySeed();
  };

  const finishCancelEdit = () => {
    // #1530: an edit's inline images belong to the edit; Cancel drops them.
    if (editing) dropStagingKey(editStagingKey(editing.messageId));
    setEditing(null);
    setDraft(draftBeforeEdit.current);
    draftBeforeEdit.current = '';
    editPrefill.current = '';
    setSendError(null);
    // The edit's refusal goes with the edit: left in place, the restored
    // draft could offer "Remove @Name and send" and post it as a new message
    // (#1599 Story 1.8 review).
    setRefusedMentionIds([]);
    bumpBodySeed();
  };

  /**
   * Cancel an edit, with the rule `cancelCompose` already follows (review,
   * D2): silently when nothing was changed, through the confirmation when it
   * was. `then` runs once the edit is gone, confirmed or not, so a caller
   * that needs the composer back (Back, a tab switch, a second Edit) can
   * continue from there.
   */
  const requestCancelEdit = (then?: () => void) => {
    const go = () => {
      finishCancelEdit();
      then?.();
    };
    if (editDirty) {
      setConfirmCancelEdit(() => go);
      return;
    }
    go();
  };

  /**
   * Begin correcting one of the viewer's own messages (Story 1.3, D2): the
   * ONE composer becomes the edit box. Only inside an open thread and never
   * over a compose in progress. The draft that was there is kept for Cancel.
   * A second Edit while one is open goes through the cancel rule first, so
   * the reply that was in the box is kept and a dirty edit is not thrown
   * away without asking (review).
   */
  const beginEdit = (message: Message) => {
    if (!openThreadId || composingThread || submittingRef.current) return;
    if (editing) {
      if (editing.messageId === message.id) return;
      const original = draftBeforeEdit.current;
      requestCancelEdit(() => startEdit(message, original));
      return;
    }
    startEdit(message, draft);
  };

  /**
   * Confirmed through the dialog (AC 28, 36). A refusal shows in the unsent
   * line. Guarded by the same ref as the other writes (review): a second
   * press inside one tick issued a second DELETE, whose `404` then told the
   * user the message was "still here". And if the message withdrawn was the
   * one being corrected, the edit has nothing left to save into (review).
   */
  const confirmDeleteMessage = async () => {
    const target = confirmDelete;
    setConfirmDelete(null);
    if (!target || !openThreadId || submittingRef.current) return;
    submittingRef.current = true;
    setSendError(null);
    try {
      const ok = await deleteMessage(openThreadId, target.id);
      if (!ok) {
        setSendError(UNSENT_DELETE);
        return;
      }
      if (editing?.messageId === target.id) finishCancelEdit();
    } finally {
      submittingRef.current = false;
    }
  };

  const cancelCompose = () => {
    // Discard silently only when there is nothing to discard.
    if (!newTitle.trim() && !draft.trim()) {
      setComposingThread(false);
      return;
    }
    setConfirmDiscard(true);
  };

  const discardCompose = () => {
    setComposingThread(false);
    setNewTitle('');
    setDraft('');
    setSendError(null);
    setConfirmDiscard(false);
    // #1530: a discarded first message's inline images go with it.
    dropStagingKey(COMPOSE_STAGING_KEY);
    bumpBodySeed();
  };

  /**
   * Leaving the open thread takes the composer that was holding the failed
   * message with it, so the unsent line has to go too — it reads "it is still
   * in the box below", and in the thread list there is no box, and the button
   * beneath it says Send and would create a NEW thread rather than retry.
   *
   * The draft text itself is deliberately KEPT. Leaving a thread is not a
   * discard action, and this change's whole point is that typed text is not
   * thrown away without asking.
   */
  // --------------------------------------------------------------------------
  // The rename's three verbs (#1069 Story 7.3, D6).
  // --------------------------------------------------------------------------
  /**
   * ⚠️ CANCEL HAS TO ANNOUNCE ITSELF BEFORE THE BLUR IT CAUSES (review patch
   * P1). Under react-native-web, pressing Cancel moves focus out of the field,
   * so the field's `onBlur` -- which COMMITS a changed value (D6) -- runs
   * BEFORE Cancel's `onPress`. Without this ref a changed title was saved by
   * the act of cancelling, and `cancelRename()` then closed an editor that had
   * already committed. The ref is set in `onPressIn`, which fires on
   * touch/mouse DOWN, before focus moves anywhere.
   */
  const cancelRenameIntentRef = useRef(false);

  const startRename = () => {
    cancelRenameIntentRef.current = false;
    setRenameFailed(false);
    setRenameFocus(true);
    setRenameDraft(openThread ? openThread.title : '');
  };

  const cancelRename = () => {
    cancelRenameIntentRef.current = false;
    setRenameDraft(null);
  };

  /**
   * Enter, Save, or blur WITH a change (D6).
   *
   * ⚠️ AN UNCHANGED OR EMPTY VALUE SENDS NO REQUEST and simply closes the
   * editor. "Unchanged" is equality after `trim()`, which also makes a
   * whitespace-only entry a cancel rather than a 400 the user has to read.
   *
   * ⚠️ AND THE NEW TITLE IS NEVER APPLIED LOCALLY. `renameThread` resolves only
   * once the threads query carries it (D7), and the header renders
   * `openThread.title` off that query -- so the editor closing IS the whole of
   * the update, and a failure leaves the old title on screen because it never
   * left.
   */
  const commitRename = async () => {
    // P1: a cancel that is already under way wins over the blur it caused. The
    // flag is consumed here (and reset on EVERY path) so it can never leak into
    // the next commit.
    const cancelling = cancelRenameIntentRef.current;
    cancelRenameIntentRef.current = false;
    if (cancelling) {
      setRenameDraft(null);
      return;
    }
    // Offline the editor is not rendered (#1069 offline state), and the blur
    // its unmount can fire must not send a write nor clear the draft: the
    // editor comes back with it on reconnect, the way the composer's does.
    if (!online || !networkService.isConnected()) return;
    if (renameCommitting) return;
    const threadId = openThreadId;
    const next = (renameDraft ?? '').trim();
    if (!threadId || next === '' || next === (openThread?.title ?? '')) {
      setRenameDraft(null);
      return;
    }
    // ⚠️ THE EDITOR STAYS UP WHILE THE COMMIT IS ON THE WIRE (review patch P2).
    // It used to close here, which made the in-flight guard invisible: the
    // pencil came back mid-flight and reopened the editor seeded with the STALE
    // title, and a later Save renamed the conversation straight back. The field
    // stays, Save is disabled, and the pencil is withheld until the write lands.
    setRenameCommitting(true);
    const ok = await renameThread(threadId, next);
    // P3: a resolved rename belongs to the conversation that STARTED it. If the
    // panel has moved on (or gone), the result is dropped rather than written
    // onto whatever is open now.
    if (!renameMountedRef.current || openThreadIdRef.current !== threadId) {
      return;
    }
    setRenameCommitting(false);
    // A rename that failed because the device dropped keeps the typed name
    // and says nothing (#1069 offline state): the editor is hidden offline
    // and comes back with it on reconnect, the way the composer's draft does.
    if (!ok && !networkService.isConnected()) return;
    setRenameDraft(null);
    // ⚠️ A FLAG, NOT A CAPTURED STRING (D8). `writeRefusal` is the hook's and
    // is set by `onError` one render later than this line resumes, so reading
    // it here would capture the PREVIOUS refusal (or `null`). The line is
    // rendered from the current `writeRefusal` instead, which is what D8's
    // `writeRefusal ?? 'Could not rename the conversation'` says.
    setRenameFailed(!ok);
  };

  const leaveOpenThread = (before?: () => void) => {
    const leave = () => {
      before?.();
      setOpenThreadId(null);
      setSendError(null);
      setConfirmDelete(null);
      // Story 7.3, D8: the rename's editor and its line belong to the thread
      // being left, and are cleared with it -- on Back, on a tab switch, and
      // therefore on every open too, since opening follows a leave.
      setRenameDraft(null);
      setRenameFailed(false);
      // Story 7.4, D11: the strip remembers nothing between opens. Same
      // place, same reason as the rename editor above it -- on Back, on a tab
      // switch, and therefore on every open too, since opening follows a
      // leave. A strip left open would also mean the region is measured
      // smaller for the NEXT conversation before its own listing has arrived.
      setFilesExpanded(false);
      // Story 7.6, D7: a pull in flight belonged to the conversation being
      // left, and so did its line. The pages it loaded are kept.
      cancelJump();
      setJumpError(null);
      // Story 7.5, D14/AC 16: so did a zip in flight and its line.
      saveAll.cancel();
    };
    // An edit in progress belongs to the thread being left; the draft it
    // displaced is restored, the way Cancel restores it, and a dirty edit is
    // asked about first, the way Cancel asks (review, D2). `before` (a tab's
    // `setSpace`) waits with it, so the thread is never left open under the
    // other tab's audience while the question is up.
    if (editing) {
      requestCancelEdit(leave);
      return;
    }
    leave();
  };

  /**
   * Where the gallery's bytes come from (GitLab #1539): the Story 8.3 cache
   * in front of the authenticated, thread-scoped fetch.
   *
   * ⚠️ ONLY A FILE THIS APP CAN RENDER COSTS A FETCH (Story 4.2). The
   * carousel never asks for an item marked `previewable: false`; `null` here
   * is the second guard, for a source handed such an item directly. The
   * carousel's `onScreenId` is the cache's `keep`: the file on screen is
   * never evicted, however the reader got there (8.3 D6).
   *
   * ⚠️ DOWNLOAD ONLY ONLINE. The unsupported page shows no Download button
   * without a handler, which is the offline rule the old dialog spelled as
   * `hideDownload` (#1069 offline state): no bytes and no network, nothing
   * to save.
   */
  /** The connection, read at call time so the source object stays stable. */
  const liveOnline = useRef(online);
  liveOnline.current = online;

  const galleryLoad = useCallback<CarouselSource['load']>(
    async (item, { onScreenId }) => {
      const galleryItem = item as GalleryItem;
      if (openThreadId == null || !galleryItem.previewable) return null;
      const cached = fileCache.peek(galleryItem.id, { keep: onScreenId });
      if (cached) return cached;
      // ⚠️ OFFLINE, A MISS IS "NOT NOW", NOT A FAILURE (#1069 offline state:
      // every error line is held back while the device has no connection).
      // The page keeps its thumbnail and spinner; the reconnect re-renders
      // the panel, the carousel asks again, and the file loads.
      if (!liveOnline.current) return null;
      // ⚠️ A NEIGHBOUR THAT CANNOT SIT BESIDE THE FILE ON SCREEN UNDER THE
      // CEILING IS NOT FETCHED AHEAD (8.3 D2's size rule, kept): it would
      // be turned away at insert, or evict what the reader is looking at.
      // The on-screen item is always fetched.
      if (galleryItem.id !== onScreenId) {
        const onScreen = galleryRef.current?.items.find(
          i => i.id === onScreenId,
        );
        const onScreenBytes = onScreen?.sizeBytes ?? 0;
        if (
          galleryItem.sizeBytes! > MESSAGE_FILE_CACHE_BYTES ||
          onScreenBytes + galleryItem.sizeBytes! > MESSAGE_FILE_CACHE_BYTES
        ) {
          return null;
        }
      }
      const threadId = String(openThreadId);
      return fileCache.getOrFetch(
        galleryItem.id,
        () =>
          fetchMessageFile({
            threadId,
            // ⚠️ THE ITEM'S OWN `messageId`, NOT THE SET'S (Story 7.4, D4).
            messageId: galleryItem.messageId,
            fileId: galleryItem.id,
          }),
        { keep: onScreenId },
      );
    },
    [openThreadId, fileCache],
  );

  const gallerySource = useMemo<CarouselSource>(
    () => ({
      load: galleryLoad,
      // The button must be ABSENT offline, not a no-op: presence is the
      // only thing this object changes on, and the carousel reads the
      // source through a ref so the flip aborts no load.
      download: online
        ? item => {
            const galleryItem = item as GalleryItem;
            void downloadAttachmentFile(
              galleryItem.messageId,
              galleryItem.file,
            );
          }
        : undefined,
    }),
    [galleryLoad, online, downloadAttachmentFile],
  );

  /**
   * What the unsent line says (Story 1.3, AC 37): the server's own explanation
   * when the last write was refused with one (a 403's reason, a 400's reason),
   * otherwise the panel's generic copy for the situation.
   */
  const unsentLine = sendError
    ? sendError === SENT_IN_PREVIOUS_TAB
      ? sendError
      : writeRefusal ?? sendError
    : null;

  /**
   * One line for the panel, and the unsent message outranks the rest.
   *
   * A failed file OPEN no longer lands here (GitLab #1539): the gallery shows
   * it on the page itself, so a 404 on image 3 of 7 keeps the counter and the
   * arrows and the reader's place.
   *
   * Story 7.6, D9: the jump's failure is the second operand. The writers
   * first (a refused post is what the reader just caused), the jump after --
   * the hook's own error precedence (6.4 D4). The line
   * reads "the last thing you tried to open did not open", which is true of a
   * message too.
   *
   * Story 7.5, D14: the save's failure is the fourth operand, one further
   * down. The 413's reason arrives verbatim from the server (or from the
   * client's own `zipTooLargeReason` when it refused locally); an abort sets
   * nothing. Cleared on the next start, on the strip's collapse, and in
   * `leaveOpenThread`.
   */
  /**
   * ⚠️ HELD BACK WHILE THE DEVICE IS OFFLINE, AND UNTIL THE RECONNECT HAS
   * SETTLED (#1069 offline state, `showErrors`). Offline is a state, not a
   * failure, so no error line speaks for it; the reconnect clears what the
   * offline period recorded. Online, every line here is exactly what it was
   * (the spec's R8).
   */
  /** The refused tokens' names, from the draft, in document order. */
  const refusedMentionNames = useMemo(
    () =>
      refusedMentionIds.length > 0
        ? mentionTokenNames(draft, refusedMentionIds)
        : [],
    [draft, refusedMentionIds],
  );
  /**
   * Whether "Remove @Name and send" would send anything: a draft that is
   * nothing but the refused tokens strips to an empty body, which the post
   * and create paths drop without a word. Then the button is not offered and
   * the sentence alone stands (review 2026-10-22).
   */
  const removeAndSendSendsSomething =
    refusedMentionNames.length > 0 &&
    (messageTextLength(stripMentionTokens(draft, refusedMentionIds)) > 0 ||
      (openThreadId != null && stagedFiles.length > 0) ||
      // An edit whose message already holds files may save an empty caption
      // (the server's C4, `postDraft`'s `editedHoldsFiles`).
      (editing != null &&
        (messages.find(m => m.id === editing.messageId)?.attachments?.length ??
          0) > 0));
  const panelErrorLine = showErrors
    ? unsentLine ?? jumpError ?? saveAll.failure
    : null;
  /**
   * Nothing loaded for the current view (#1069 offline state): the list with
   * no rows, or a conversation with no messages and no pending post. Offline,
   * that is where the neutral empty state stands in for a spinner that will
   * never finish, or for "No discussions yet." that nobody can know is true.
   * Whatever IS loaded stays on screen, read-only: one rule, no mode flag.
   */
  const nothingLoaded = openThreadId
    ? messages.length === 0 && pending.length === 0
    : threads.length === 0;
  const showOfflineEmpty = !online && nothingLoaded;

  const internal = activeSpace === 'internal';
  const docked = presentation === 'docked';

  // --------------------------------------------------------------------------
  // The rename pencil's gate and reveal (#1069 Story 7.3, AC 13-14).
  // --------------------------------------------------------------------------
  /**
   * ⚠️ `Platform.OS` PER RENDER, never at module scope (Story 7.2's lesson:
   * `setupTests.ts` resolves a module-scope read to `'ios'` before any suite's
   * `beforeEach` can set `'web'`, which freezes the web branch off for the life
   * of the process).
   */
  const isWebPanel = Platform.OS === 'web';
  /**
   * ⚠️ `=== true`, AND THE ONLY INPUT IS THE SERVER'S (D5, C3). There is no
   * client-side write-permission derivation anywhere in this panel and there
   * must not be one; `mayWrite` is computed by the listing gate with the same
   * predicate the write gate runs. `undefined` means "not computed" and shows
   * no pencil.
   */
  const showRenamePencil =
    // Offline hides every write control (#1069 offline state).
    online &&
    openThread !== null &&
    openThread.mayWrite === true &&
    renameDraft === null &&
    // P2: while a commit is on the wire there is nothing to reopen -- a pencil
    // here would seed a second editor with the title the write is replacing.
    !renameCommitting;
  const pencilRevealed = !isWebPanel || headerHovered || pencilFocused;
  /**
   * ⚠️ THE PENCIL'S SIZE IS READ PER RENDER TOO (AC 14, review patch P4). It
   * lived in `StyleSheet.create` as `Platform.OS === 'web' ? 24 : …`, which is a
   * MODULE-SCOPE read: `setupTests.ts` resolves it to `'ios'` before any suite's
   * `beforeEach` can set `'web'`, so the 24px web branch was unreachable for the
   * life of the process and AC 13's sizing was unverifiable. Same lesson as
   * Story 7.2's `ResizeGrip`.
   */
  const pencilSize = isWebPanel ? 24 : spacing.minimumTapTarget;
  const pencilOpacity = pencilRevealed
    ? 1
    : coarsePointer
    ? MESSAGE_ACTIONS_COARSE_OPACITY
    : 0;

  // A docked panel that is not visible is one `CorrespondenceDock` is still
  // collapsing (it keeps the child mounted until the animation finishes, so
  // the content does not vanish mid-motion). It renders as it was; `visible`
  // has already switched every hook and effect above off (code review
  // 2026-09-11, replacing an early `return null` that blanked the animation).

  const body = (
    <View
      style={[
        styles.panel,
        docked ? styles.panelDocked : [styles.panelOverlay, overlayInsetStyle],
      ]}
      testID="correspondence-panel"
      /* Docked and not visible is the dock collapsing (second pass,
         2026-09-11): the body stays mounted for the animation, and must be
         INERT, not merely off: the dock keeps `pointerEvents` on while it
         renders, and `postMessage` has no `enabled` check of its own. */
      pointerEvents={docked && !visible ? 'none' : 'auto'}
      accessibilityElementsHidden={docked && !visible}
      importantForAccessibility={
        docked && !visible ? 'no-hide-descendants' : 'auto'
      }
    >
      {/* ---- header ----
          FOUR SIBLINGS, no wrapper (Story 5.4, AC 1; C2 — three until Story
          7.3 added the rename pencil). The chevron, the title stack, the
          pencil and the close control are direct children of the row so the
          three fixed-width controls can be told not to shrink and the stack
          can be told it may (`minWidth: 0`). With the old left-hand wrapper
          the stack had `flex: 1` and no `minWidth`, so a long subject grew its
          intrinsic width and pushed the close button on web.

          The hover flag is on the ROW, not on the title (D10): the pencil sits
          beside the title, and a pointer travelling from one to the other must
          not un-reveal it on the way. */}
      <View
        style={styles.header}
        testID="correspondence-header"
        {...(isWebPanel
          ? ({
              onMouseEnter: () => setHeaderHovered(true),
              onMouseLeave: () => setHeaderHovered(false),
            } as Record<string, unknown>)
          : {})}
      >
        {openThreadId ? (
          <TouchableOpacity
            onPress={() => leaveOpenThread()}
            style={styles.backBtn}
            testID="correspondence-back"
            accessibilityLabel="Back to discussions"
          >
            <ChevronLeft size={20} color={colors.textPrimary} />
          </TouchableOpacity>
        ) : null}
        <View style={styles.headerTitleWrap}>
          {/* Offline the editor gives way to the title and keeps its draft
              (#1069 offline state): a Save that cannot reach the server is a
              write control, and hidden beats disabled. */}
          {renameDraft === null || !online ? (
            <Text
              style={[
                styles.headerTitle,
                // 15 in a thread, 16 in the list (AC 2): a subject is a
                // sentence, the list's word is a label. Keyed on
                // `openThreadId`, not `openThread`, for the same reason the
                // thread view is.
                openThreadId ? styles.headerTitleThread : null,
              ]}
              numberOfLines={1}
              testID="correspondence-thread-title"
            >
              {openThread ? openThread.title : 'Discussion'}
            </Text>
          ) : (
            /* Story 7.3, D11: the field REPLACES the title in place, styled as
               the composer's subject at the header's own 15px. The subtitle
               line below is untouched. */
            <View style={styles.renameRow}>
              <TextInput
                style={styles.renameInput}
                value={renameDraft}
                onChangeText={setRenameDraft}
                autoFocus={renameFocus}
                selectTextOnFocus={renameFocus}
                maxLength={MAX_TITLE_LENGTH}
                returnKeyType="done"
                blurOnSubmit={false}
                onSubmitEditing={() => {
                  void commitRename();
                }}
                /* Blur WITH a change commits, blur with none cancels (D6): on
                   web clicking Save blurs the field first, so a
                   blur-that-cancels would make Save unreachable. */
                onBlur={() => {
                  onComposerFocusChange?.(false);
                  void commitRename();
                }}
                /* D12: the docked web host stands its scroll allowance down
                   for ANY text field in the panel, not only the composer's. */
                onFocus={() => onComposerFocusChange?.(true)}
                /* Escape cancels, WEB ONLY, and through `onKeyPress` rather
                   than `onKeyDown`: react-native-web's TextInput overwrites
                   `onKeyDown` with its own handler and calls `onKeyPress` from
                   inside it with the raw keydown (`Composer.tsx:1378-1383`).
                   Native has no Escape; it gets the Cancel control (D6). */
                {...(isWebPanel
                  ? ({
                      onKeyPress: (event: {
                        nativeEvent?: { key?: string };
                        preventDefault?: () => void;
                      }) => {
                        if (event?.nativeEvent?.key === 'Escape') {
                          event.preventDefault?.();
                          cancelRename();
                        }
                      },
                    } as Record<string, unknown>)
                  : {})}
                testID="correspondence-rename-thread-input"
                accessibilityLabel="Conversation name"
              />
              <TouchableOpacity
                onPress={() => {
                  void commitRename();
                }}
                /* P2: the one-commit-at-a-time guard, made VISIBLE. The
                   `renameCommitting` early return in `commitRename` is the
                   mechanism; a control that still looks pressable while it
                   refuses to do anything is a guard nobody can see. */
                disabled={renameCommitting}
                style={styles.renameAction}
                testID="correspondence-rename-thread-save"
                accessibilityRole="button"
                accessibilityState={{ disabled: renameCommitting }}
                accessibilityLabel="Save name"
              >
                <Text style={styles.renameActionText}>Save</Text>
              </TouchableOpacity>
              <TouchableOpacity
                onPress={cancelRename}
                /* P1: DOWN, not press. On web the field's blur fires before
                   this control's `onPress`, and blur-with-a-change commits
                   (D6) -- so Cancel has to declare its intent before it moves
                   focus, or cancelling saves. */
                onPressIn={() => {
                  cancelRenameIntentRef.current = true;
                }}
                style={styles.renameAction}
                testID="correspondence-rename-thread-cancel"
                accessibilityRole="button"
                accessibilityLabel="Cancel rename"
              >
                <Text style={styles.renameActionText}>Cancel</Text>
              </TouchableOpacity>
            </View>
          )}
          {renameFailed && showErrors ? (
            <Text
              style={styles.renameError}
              testID="correspondence-rename-error"
            >
              {writeRefusal ?? RENAME_FAILED_TEXT}
            </Text>
          ) : null}
          {/* `{label} · N people` for the ACTIVE space (AC 3, C11): it answers
              "who sees what I post here", so for an internal viewer it
              changes with the tab. Before the team has loaded, on a team-load
              error and after a revoke the count is NOT KNOWN (`teamCountKnown`;
              the requestor alone would still count as 1), and the label stands
              alone rather than guessing. */}
          <Text style={styles.headerSub} numberOfLines={1}>
            {headerSubtitle(
              requestLabel,
              teamCountKnown ? spaceMembers.length : 0,
            )}
          </Text>
        </View>
        {/* Story 7.3, AC 13: the fourth sibling, and ABSENT rather than hidden
            unless the LISTING says this caller may write. `=== true` fails
            closed: `undefined` (a provider that does not answer the question)
            shows no pencil. */}
        {/* `Pressable`, not `TouchableOpacity`: `pointerEvents` is a
            `Pressable` prop here, and `MessageActions` uses one for the same
            reveal. */}
        {showRenamePencil ? (
          <Pressable
            onPress={startRename}
            style={[
              styles.renameBtn,
              { width: pencilSize, height: pencilSize, opacity: pencilOpacity },
            ]}
            /* Invisible must also mean intangible (D10, copying
               `MessageActions.tsx:824`): an `opacity: 0` control is still
               hit-tested and would eat clicks beside the title. */
            pointerEvents={pencilRevealed || coarsePointer ? 'auto' : 'none'}
            onFocus={() => setPencilFocused(true)}
            onBlur={() => setPencilFocused(false)}
            hitSlop={{ top: 6, bottom: 6, left: 6, right: 6 }}
            testID="correspondence-rename-thread"
            accessibilityRole="button"
            accessibilityLabel="Rename conversation"
          >
            <Pencil size={16} color={colors.textSecondary} />
          </Pressable>
        ) : null}
        <TouchableOpacity
          onPress={onClose}
          style={styles.closeBtn}
          testID="correspondence-close"
          accessibilityLabel="Close discussion"
        >
          <X size={18} color={colors.textSecondary} />
        </TouchableOpacity>
      </View>

      {/* ---- space tabs (internal users only) ---- */}
      {canSeeInternal ? (
        <View
          style={styles.tabs}
          accessibilityRole="tablist"
          accessibilityLabel="Discussion audience tabs"
        >
          <ScrollView
            horizontal
            showsHorizontalScrollIndicator={false}
            style={styles.tabScroller}
            contentContainerStyle={styles.tabRow}
          >
            <SpaceTab
              /* "Vertiv", not "Internal" (Story 1.2, C3; UX-DR11): the audience
               is named for who is in it, and the hint carries the consequence. */
              label="Vertiv"
              hint="Not visible to the customer"
              icon={
                <Lock
                  size={14}
                  color={internal ? colors.primaryStrong : colors.textSecondary}
                />
              }
              unread={tabUnread.internal}
              active={internal}
              onPress={() => leaveOpenThread(() => setSpace('internal'))}
              testID="correspondence-tab-internal"
            />
            <SpaceTab
              label="Shared"
              hint="The customer can read this"
              icon={
                <Users
                  size={14}
                  color={
                    !internal ? colors.primaryStrong : colors.textSecondary
                  }
                />
              }
              unread={tabUnread.shared}
              active={!internal}
              onPress={() => leaveOpenThread(() => setSpace('shared'))}
              testID="correspondence-tab-shared"
            />
          </ScrollView>
        </View>
      ) : (
        /* The testID is what makes "no tabs" a non-vacuous assertion (Story
           1.4, C6): a suite asserts this note RENDERED before asserting the
           tabs' absence, so a panel that never mounted cannot pass. */
        <View
          style={styles.singleSpaceNote}
          testID="correspondence-single-space-note"
        >
          <Users size={13} color={colors.textMuted} />
          <Text style={styles.singleSpaceText}>
            Discussion with the Vertiv team
          </Text>
        </View>
      )}

      {/* ---- who can read this space ---- */}
      {/* Internal viewers only. "2 people can read this, including 1
              outside Vertiv" is internal-facing language: it describes the
              team's composition, which a customer has no business reading. */}
      {canSeeInternal ? (
        <TeamStrip
          members={spaceMembers}
          space={activeSpace}
          teamStatus={teamStatus}
        />
      ) : null}

      {/* ---- provider error. Surfaced, never swallowed: a vendor failing
                   here is the POC finding, not something to hide. ---- */}
      {/* Offline suppresses every error bar (#1069 offline state): a request
          that failed because the device has no connection is not news. */}
      {teamError && canSeeInternal && showErrors ? (
        <View style={styles.errorBar} testID="correspondence-team-error">
          <Text style={styles.errorTitle}>Could not load the request team</Text>
          <Text style={styles.errorBody}>{teamError}</Text>
        </View>
      ) : null}

      {/* Raw vendor errors go to internal viewers only. Stream's code-70
              message literally disclosed that an inaccessible channel existed
              (POC finding #3) — a diagnostic surface leaks as readily as a
              feature surface. External viewers get a neutral line. */}
      {error && showErrors ? (
        canSeeInternal ? (
          <View style={styles.errorBar} testID="correspondence-error">
            {/* ⚠️ INTERNAL VIEWERS ONLY (NFR31): a diagnostic leaks as
                readily as a feature surface. This used to read "Backend error
                (api)", naming the one and only backend, which told the reader
                nothing once the arm-selection machinery went. */}
            <Text style={styles.errorTitle}>Discussion error</Text>
            <Text style={styles.errorBody}>{error}</Text>
          </View>
        ) : (
          <View style={styles.errorBar} testID="correspondence-error">
            <Text style={styles.errorBody}>
              Something went wrong loading this discussion.
            </Text>
          </View>
        )
      ) : null}

      {/* ---- the conversation's files (#1069 Story 7.4, FR77) ----

          ⚠️ ABOVE THE MEASURED REGION AND BELOW THE ERROR BARS, WHICH IS A
          DECISION AND NOT A PLACEMENT (C11). The region is the ONE
          measurement the composer's drag is bounded by, and opening the strip
          SHOULD shrink it: the composer's maximum then re-derives from the
          region's own `onLayout`, which is exactly the path the `TeamStrip`'s
          "Show" already takes. Put inside `MessageList` as a
          `ListHeaderComponent` instead, this would be a fetch-driven,
          expanding block at the TOP of an inverted-order list that already
          carries `maintainVisibleContentPosition`, a settle timer and the
          `scrollToEnd` trap -- the surface Story 7.6 is last in the epic for.

          ⚠️ ABSENT, NOT DISABLED, WHENEVER THERE IS NOTHING TO SHOW (AC 15):
          no conversation open, the listing still loading, the listing FAILED
          (D8: silent), zero files, or access revoked. "When n is 0 the
          control is absent" extends to "when n is unknown".

          ⚠️ `!threadFiles`, NOT `threadFiles === null`. The hook's contract
          says `null` and means it, but a suite that mocks `useCorrespondence`
          with an object literal hands back `undefined` for a member added
          after the mock was written -- which is a crash rather than an absent
          row, in a suite that is not about files at all. The stale mock is
          fixed too (`CorrespondenceDock.test.tsx`); this is the guard that
          makes the next one a missing row instead of a red screen.

          Story 7.6: every item in the strip JUMPS to its message; nothing in
          it opens a file (D2). While older pages are being pulled for that
          jump the chevron's slot shows a spinner and the row is `busy` (D8);
          pressing the row then cancels the pull with the collapse (D7).

          Story 7.5 (D10, C8): "Save all" is the row's SIBLING, not a child of
          the toggle -- a control inside a pressable is a nested press. The
          toggle keeps every prop 7.4 and 7.6 pinned; it and the Save-all
          button sit in `filesRow`. ABSENT, not disabled, under two files
          (one file's Save all IS that file's save button) and whenever the
          row itself is absent. While a zip is in flight the button is Cancel,
          the row's own text carries the percent ("Files (7) · Saving 42%",
          or "Saving…" when the listing is truncated and there is no honest
          denominator, D12), and a 2px bar sits ABSOLUTELY on the row's bottom
          edge. Nothing gains height: the row is above the measured region
          and its height must not move (7.4 C11, 7.6 D8). Collapsing the strip
          does NOT cancel a save; it only clears the save's line (D14). */}
      {/* Offline: absent, for both halves (#1069 offline state). Save all is
          a download, and every row in the strip is a jump that may have to
          pull older pages from the server. */}
      {accessRevoked ||
      !online ||
      !threadFiles ||
      threadFiles.items.length === 0
        ? null
        : (() => {
            const label = filesExpanded ? 'Collapse files' : 'Expand files';
            const pulling = jump.pendingMessageId !== null;
            const count = threadFiles.truncated
              ? `${MAX_THREAD_FILE_LISTING}+`
              : `${threadFiles.items.length}`;
            const savingSuffix = saveAll.saving
              ? ` · Saving${
                  saveAll.percent === null ? '…' : ` ${saveAll.percent}%`
                }`
              : '';
            const saveAllLabel = threadFiles.truncated
              ? 'Save all files'
              : `Save all ${threadFiles.items.length} files`;
            return (
              <>
                <View style={styles.filesRow} testID="correspondence-files-row">
                  <TouchableOpacity
                    testID="correspondence-files-toggle"
                    style={styles.filesToggle}
                    onPress={() => {
                      // A collapse calls off a pull and clears its line; the
                      // pages already loaded are kept (D7, D9). It clears the
                      // save's line too, and does NOT cancel the save (7.5 D14).
                      if (filesExpanded) {
                        cancelJump();
                        setJumpError(null);
                        dismissSaveFailure();
                      }
                      animateFilesDrawer();
                      setFilesExpanded(v => !v);
                    }}
                    accessibilityRole="button"
                    // ⚠️ THE SUBSECTION CARDS' CONTRACT, NOT `TeamStrip`'s
                    // (C10). `SubsectionForm` carries `Expand {title}` /
                    // `Collapse {title}` plus `accessibilityState.expanded`, and
                    // `testing-ui-sync.md` names that as the a11y contract the
                    // unit and E2E tests share. `TeamStrip` instead puts a
                    // visible "Show"/"Hide" word in a label naming the count,
                    // with no `expanded` state at all -- which would say the
                    // same thing twice in the wrong channel.
                    accessibilityLabel={label}
                    // `busy` only while a pull is on (D8): the object is
                    // `{ expanded }` alone otherwise, which is what 7.4 pinned.
                    accessibilityState={
                      pulling
                        ? { expanded: filesExpanded, busy: true }
                        : { expanded: filesExpanded }
                    }
                    // ⚠️ AND `aria-expanded`, WHICH IS NOT THE SAME PROP SAID
                    // TWICE (code review, 2026-09-18). MEASURED in
                    // `react-native-web/dist/modules/createDOMProps/index.js`:
                    // `accessibilityState` appears ZERO times, and
                    // `aria-expanded` is derived from the `aria-expanded` or
                    // `accessibilityExpanded` props alone. So the line above
                    // emitted NO DOM attribute, `correspondence.page.ts`'s
                    // `expandFiles` read `null` from `getAttribute` and clicked
                    // an already-open strip shut. `FormDropdown.tsx` passes it
                    // explicitly for exactly this reason; the state prop stays
                    // because it is what native reads.
                    aria-expanded={filesExpanded}
                  >
                    <Paperclip size={13} color={colors.textSecondary} />
                    <Text style={styles.filesToggleText} numberOfLines={1}>
                      {`Files (${count})${savingSuffix}`}
                    </Text>
                    {pulling ? (
                      // The chevron's slot is the one place on the row that
                      // means "state", so the spinner reads as "working on it"
                      // without a new line; the row's height is unchanged.
                      <ActivityIndicator
                        size="small"
                        color={colors.textSecondary}
                        testID="correspondence-files-jump-spinner"
                      />
                    ) : filesExpanded ? (
                      <ChevronUp size={14} color={colors.textSecondary} />
                    ) : (
                      <ChevronDown size={14} color={colors.textSecondary} />
                    )}
                  </TouchableOpacity>
                  {saveAll.saving ? (
                    <TouchableOpacity
                      testID="correspondence-files-save-cancel"
                      style={styles.filesSaveButton}
                      onPress={saveAll.cancel}
                      accessibilityRole="button"
                      accessibilityLabel="Cancel saving files"
                    >
                      <X size={14} color={colors.textSecondary} />
                    </TouchableOpacity>
                  ) : threadFiles.items.length >= 2 ? (
                    <TouchableOpacity
                      testID="correspondence-files-save-all"
                      style={styles.filesSaveButton}
                      onPress={saveAll.start}
                      accessibilityRole="button"
                      accessibilityLabel={saveAllLabel}
                    >
                      <Download size={14} color={colors.textSecondary} />
                    </TouchableOpacity>
                  ) : null}
                  {saveAll.saving && saveAll.percent !== null ? (
                    // D12: a measurement, never an animation. Absent (not at
                    // 0) when the percent is null.
                    <View style={styles.filesSaveTrack} pointerEvents="none">
                      <View
                        testID="correspondence-files-save-progress-fill"
                        style={[
                          styles.filesSaveFill,
                          { width: `${saveAll.percent}%` },
                        ]}
                      />
                    </View>
                  ) : null}
                </View>
                {filesExpanded ? (
                  <ThreadFilesStrip
                    files={threadFiles.items}
                    onSelect={selectStripFile}
                    onSave={saveStripFile}
                  />
                ) : null}
              </>
            );
          })()}

      {/* ---- the measured region: the message list and the composer, and
               NOTHING ELSE ABOVE THEM ----

          ⚠️ THE ONE CONTAINER (#1069 UI-8). One `onLayout`, in one component,
          on a box the drag cannot resize — see `onComposerRegionLayout`. The
          list keeps the `flex: 1` it always had, so the composer's height and
          the list's height still sum to this box; the difference from UI-7 is
          that the BOX is what is measured, not the two things inside it.

          The unsent-message line is INSIDE the region on purpose. Outside it,
          appearing after a failed send would shrink the region and pull the
          maximum down under a composer the user had already dragged — a bound
          moving in response to the very failure that must leave their message
          alone. Inside, it takes its space from the list like everything else
          and the maximum does not move at all. */}
      <View
        style={styles.composerRegion}
        onLayout={onComposerRegionLayout}
        testID="correspondence-composer-region"
      >
        {accessRevoked ? null : showOfflineEmpty ? (
          <OfflineEmptyState />
        ) : loadingThreads && threads.length === 0 ? (
          <View style={styles.loading}>
            <ActivityIndicator color={colors.primary} />
          </View>
        ) : openThreadId ? (
          <MessageList
            messages={messages}
            pending={pending}
            /* AC 11 (FR18): call off a post that is still going. */
            onCancelPending={cancelPending}
            viewerId={viewer?.id}
            viewerIsConfigTeam={viewerIsConfigTeam}
            viewerName={viewer?.name}
            canSeeInternal={canSeeInternal}
            loading={loadingMessages}
            // Offline: no "Load earlier messages", and no auto-load at the top
            // (both read `hasOlder`); an older page needs the network.
            hasOlder={online && olderMessagesCursor !== null}
            loadingOlder={loadingOlder}
            onLoadOlder={loadOlderMessages}
            /* Offline, no handler means no control (#1069 offline state):
               `MessageRow` offers Edit, Delete and Download only when their
               handler is wired, and a file row with nothing to open is not
               pressable. The messages themselves stay, read-only. */
            onEdit={online ? beginEdit : undefined}
            onOpenAttachment={online ? openAttachment : undefined}
            // ⚠️ THE LOOKUP LIVES HERE NOW, WHERE THE MESSAGE IS (Story 7.4,
            // D4). `MessageList` hands back the message and a file id because
            // its bubbles own their attachments; the download itself takes the
            // FILE, because the strip's items belong to no single message.
            onDownloadAttachment={
              online
                ? (message, fileId) => {
                    const file = (message.attachments ?? []).find(
                      f => f.id === fileId,
                    );
                    if (file) void downloadAttachmentFile(message.id, file);
                  }
                : undefined
            }
            onDelete={online ? setConfirmDelete : undefined}
            /* Convenience only: the server is the control (NFR8). */
            mayDeleteAny={mayDeleteAnyMessage(user?.roles)}
            editingMessageId={editing?.messageId ?? null}
            /* Story 7.6, D4: the jump, as a prop; the list performs it. */
            reveal={jump.reveal}
          />
        ) : (
          <ThreadList threads={threads} onOpen={setOpenThreadId} />
        )}

        {/* The unsent-message line sits DIRECTLY above the composer still
            holding the text, because that is where the one recovery action
            is. Since Story 1.3 it prefers the server's own explanation when
            the write was refused with one (AC 37). */}
        {panelErrorLine && !accessRevoked ? (
          <View style={styles.errorBar} testID="correspondence-send-error">
            <Text style={styles.errorBody}>{panelErrorLine}</Text>
            {/* #1599 Story 1.4 (AC 9): only beside a mention refusal, and
                only while the draft still carries a refused token. The names
                are the DRAFT's own token text, never the server's. */}
            {unsentLine !== null && removeAndSendSendsSomething ? (
              <Pressable
                onPress={removeRefusedMentionsAndSend}
                disabled={submitting}
                accessibilityRole="button"
                testID="correspondence-remove-refused-and-send"
                style={styles.errorActionHit}
              >
                <Text style={styles.errorAction}>
                  {removeRefusedLabel(
                    refusedMentionNames,
                    editing ? 'save' : 'send',
                  )}
                </Text>
              </Pressable>
            ) : null}
          </View>
        ) : null}

        {/* ---- the one composer, outside the branch above ----
            A SIBLING, not a child of either view, so the same instance (and
            the same draft) survives the swap between the list and a thread.
            Rendered on EVERY request, CLOSED included (Story 1.3, FR30): the
            closed notice that used to replace it is gone with the stage prop. */}
        {/* D3: no composer for a request the person no longer has.

            ⚠️ OFFLINE: HIDDEN, NOT DISABLED, AND ONE MUTED LINE IN ITS PLACE
            (#1069 offline state). A greyed Post reads as broken; the line
            says why and when. New conversation, the paperclip and the staged
            strip are inside the composer and go with it. The draft, the
            staged files and an edit in progress are PANEL state, so the
            composer that comes back on reconnect holds exactly what it did. */}
        {accessRevoked ? null : !online ? (
          <Text
            style={[styles.empty, styles.offlineComposerLine]}
            testID="correspondence-offline-composer-line"
          >
            {OFFLINE_COMPOSER_LINE}
          </Text>
        ) : (
          <Composer
            mode={composerMode}
            internal={internal}
            /* WHO is being spoken to, as distinct from WHICH space is on screen.
               Already computed for the tabs; the composer was the one consumer
               that never received it, which is why it addressed every viewer in
               the internal voice (#1069 UI-6). */
            canSeeInternal={canSeeInternal}
            title={newTitle}
            onChangeTitle={setNewTitle}
            body={draft}
            onChangeBody={onChangeBody}
            submitting={submitting}
            /* #1530: the editor's image resolver stages an inline file under
               the composer's key, and the seed remounts the editor when the
               text is new (an edit begins or ends, a compose is discarded). */
            onResolveImage={resolveInlineImage}
            bodySeed={bodySeed}
            /* #1599 Story 1.4: the @ suggester's data (flag, list, refused
               ids), forwarded to the editor untouched. */
            mentionSuggest={mentionSuggest}
            /* #1069 Story 4.1: the files live here, not in the composer, so a
               failed or cancelled send hands them back (AC 12, AC 13). */
            stagedFiles={stagedFiles}
            /* Keyed on the STAGING key, not the thread (#1530 review, C1): a
               reply's is the thread id, as before; an edit's and a compose's
               are their own, so an unconfirmed files edit or create shows its
               caution where its images are staged. */
            stagedMayAlreadyBeSent={
              stagingKey != null && mayAlreadyBeSent === stagingKey
            }
            /* #1530 review, C5: the server lets an edit save an EMPTY caption
               on a message that holds files; the gate must agree. */
            editedMessageHoldsFiles={
              editing != null &&
              (messages.find(m => m.id === editing.messageId)?.attachments
                ?.length ?? 0) > 0
            }
            onRemoveStagedFile={removeStagedFile}
            /* Replying only: attachments on a FIRST message are out of this
               spec, and the composer renders the paperclip in the reply shape
               alone. */
            onPickFiles={openThreadId != null ? pickFiles : undefined}
            onStartCompose={() => setComposingThread(true)}
            onCancelCompose={cancelCompose}
            onCancelEdit={() => requestCancelEdit()}
            /* Reply and Save share `post`: the mode decides which write. */
            onSubmit={openThreadId != null ? post : createThread}
            /* When the composer must forget a height it was dragged to (#1069
               UI-7): a resize is deliberately SESSION-scoped, because the
               conversation is the point and the composer is a tool you reach
               for. */
            panelOpen={visible}
            /* How far it may be dragged (#1069 UI-8) — the region above, and
               never a sum of anything. */
            availableHeight={composerRegionHeight}
            /* And when it must forget that height again: a send that WORKED. */
            heightResetSignal={sendSucceeded}
            /* Defect 3 (2026-09-15): the DOCKED host stands its scroll
               allowance down while a composer field holds focus. Undefined in
               the overlay, which is a `Modal` with its own scroller. */
            onFieldFocusChange={onComposerFocusChange}
          />
        )}
      </View>

      {/* The delete confirmation (Story 1.3, AC 28, 36): the same inline
          dialog the discard flow uses, beside it. */}
      {confirmDelete ? (
        <ConfirmationDialog
          visible
          inline
          onClose={() => setConfirmDelete(null)}
          onConfirm={confirmDeleteMessage}
          title="Delete this message?"
          message={DELETE_MESSAGE_COPY}
          confirmText="Delete"
          cancelText="Keep it"
          confirmButtonStyle="danger"
        />
      ) : null}

      {/* The cancel-edit confirmation (review, D2): the same rule and the same
          inline dialog as discarding a compose. */}
      {confirmCancelEdit ? (
        <ConfirmationDialog
          visible
          inline
          onClose={() => setConfirmCancelEdit(null)}
          onConfirm={() => {
            const go = confirmCancelEdit;
            setConfirmCancelEdit(null);
            go();
          }}
          title="Discard your changes?"
          message="The message will keep the text it has now."
          confirmText="Discard"
          cancelText="Keep editing"
          confirmButtonStyle="danger"
        />
      ) : null}

      {/* Mounted only while it is asked for, and `inline` rather than a Modal:
          a docked panel must contain no Modal, because a Modal cannot displace
          layout, which is the whole reason for docking. */}
      {confirmDiscard ? (
        <ConfirmationDialog
          visible
          inline
          onClose={() => setConfirmDiscard(false)}
          onConfirm={discardCompose}
          title="Discard this message?"
          message="What you have typed will be lost."
          confirmText="Discard"
          cancelText="Keep writing"
          confirmButtonStyle="danger"
        />
      ) : null}

      {/* ⚠️ MOUNTED ONLY WHILE OPEN, NOT MOUNTED-AND-HIDDEN. The carousel
          renders a `Modal`, and the DOCKED panel has a standing rule that it
          renders no `Modal` at all — it is an inline sibling so the request
          beside it can shrink, and a pinned test says so. Same mount/unmount
          reasoning the actions chip already uses: "it is mounted and
          unmounted, and RN never animates an unmount." Rendering it inside
          the panel keeps a file's lifetime tied to the conversation it came
          from (GitLab #1539: one gallery for every file kind). */}
      {gallery ? (
        <AttachmentCarouselModal
          visible
          items={gallery.items}
          initialIndex={gallery.initialIndex}
          sectionLabel={openThread?.title || requestLabel}
          source={gallerySource}
          onClose={closeGallery}
        />
      ) : null}
    </View>
  );

  // Docked: a plain flex sibling, so the request content beside it shrinks.
  if (docked) return body;

  /**
   * Native keeps `body` in a ROW, exactly as it was before the keyboard work.
   * `PanelKeyboardColumn` is two row containers, not a column, so the panel is
   * still a stretched row child and still gets its height from the backdrop
   * rather than from its content. The only thing that changes is that the row
   * it stretches into is shorter while the keyboard is up.
   */
  // `panelKeyboard` is null on web by construction (the module-scope loader
  // above never runs there), so this is the native test as well as the
  // module-present one. An added `!isWeb` here reads as a second guard and is
  // not one: mutation-checked 2026-09-15, removing it changes no test and no
  // behaviour.
  const overlayBody = panelKeyboard ? (
    <PanelKeyboardColumn bottomInset={overlayBottomInset}>
      {body}
    </PanelKeyboardColumn>
  ) : (
    body
  );

  const backdrop = (
    <View style={styles.backdrop}>
      {isWeb ? (
        <TouchableOpacity
          style={styles.backdropFill}
          activeOpacity={1}
          onPress={onClose}
        />
      ) : null}
      {overlayBody}
    </View>
  );

  return (
    <Modal
      visible={visible}
      transparent
      animationType={isWeb ? 'fade' : 'slide'}
      onRequestClose={onClose}
    >
      {/* The Modal's OWN provider (defect 2): a Modal is a separate native
          window, so the one at `App.tsx:1654` does not reach in here even
          though React context does. Same move, same reason, as
          `RepeatableInstanceEditModal.tsx:21-35`. */}
      {panelKeyboard ? (
        <panelKeyboard.KeyboardProvider>
          {/* #1530, the `cross-platform-ui.md` modal recipe: the editor's
              keyboard chrome needs a provider INSIDE this window (the
              `KeyboardProvider` above is that; `ModalKeyboardProvider` wraps
              the same one), `isolateChrome` so the composer's editor arms
              ITS OWN Done bar rather than the request screen's behind the
              modal, and the bar as the LAST child of the overlay so its
              containing block is the full window. The scroll ref is inert:
              the column already avoids the keyboard (`PanelKeyboardColumn`),
              and the editor only registers its dismiss with the chrome. */}
          <FormKeyboardScrollProvider
            scrollRef={inertScrollRef}
            bottomOffset={0}
            isolateChrome
          >
            <View style={styles.backdrop}>
              {overlayBody}
              <RichTextKeyboardDismissBar />
            </View>
          </FormKeyboardScrollProvider>
        </panelKeyboard.KeyboardProvider>
      ) : (
        backdrop
      )}
    </Modal>
  );
};

const SpaceTab: React.FC<{
  label: string;
  hint: string;
  icon: React.ReactNode;
  active: boolean;
  onPress: () => void;
  testID: string;
  unread?: boolean;
}> = ({ label, hint, icon, active, onPress, testID, unread }) => (
  <TouchableOpacity
    onPress={onPress}
    style={[styles.tab, active && styles.tabActive]}
    testID={testID}
    accessibilityRole="tab"
    accessibilityState={{ selected: active }}
    /* ⚠️ EXTENDS THE EXISTING LABEL RATHER THAN REPLACING IT. This tab already
       had one, so unlike the thread and request rows there is a string to
       append to: `Internal space. Not visible to the customer. New.`
       ⚠️ THE SPOKEN WORD IS THE WORD ON THE BADGE, not a synonym. It used to
       say "Unread", which was right while the tab carried a wordless dot and
       is a contradiction now that it carries the word "New" (#1069 P3.3). The
       surfaces that still carry a DOT keep saying "unread": nothing visible
       there disagrees with it. */
    accessibilityLabel={`${label} space. ${hint}${
      unread ? `. ${UNREAD_BADGE_WORD}` : ''
    }`}
  >
    {/* ⚠️ ONE ROW, AND `hint` IS STILL A PROP. The hint used to render as a
        second line of 10px text beneath the label; the owner removed it on
        2026-09-18 (Story 7.1, D5) and the string moved into
        `accessibilityLabel` ABOVE, verbatim (D7). A screen reader still hears
        "Vertiv space. Not visible to the customer." while nothing on screen
        says it — which is the point, because the consequence matters most to
        the user who cannot see the lock icon. Do not "tidy up" the unused-
        looking prop. */}
    <View style={styles.tabTop}>
      {icon}
      {/* `numberOfLines` caps the label's HEIGHT, and that is all it does —
          there is no `flexShrink` here and there never was; `flex: 1` on the
          tab was providing that until D2 removed it. The row's width is kept
          safe by `tabScroller` instead, deliberately, so the audience word is
          never ellipsized. See that docblock. */}
      <Text
        style={[styles.tabLabel, active && styles.tabLabelActive]}
        numberOfLines={1}
      >
        {label}
      </Text>
      <UnreadBadge unread={unread} testID={`${testID}-unread`} />
    </View>
  </TouchableOpacity>
);

/**
 * What the panel says where it has nothing loaded and the device is offline
 * (#1069 offline state). Exported so the suite asserts the words the panel
 * renders rather than a copy of them.
 */
export const OFFLINE_EMPTY_TEXT =
  "Discussions are available when you're online.";
/** The one line where the composer stands while offline. */
export const OFFLINE_COMPOSER_LINE = "You can post once you're back online.";

/**
 * The offline empty state: a muted `CloudOff` and one sentence, in the list's
 * own empty tones. ⚠️ NO RED: this is not an error surface, and it renders for
 * internal and external viewers alike because it discloses nothing.
 */
const OfflineEmptyState: React.FC = () => (
  <View style={styles.offlineEmpty} testID="correspondence-offline-empty">
    <CloudOff size={20} color={colors.textMuted} />
    <Text style={[styles.empty, styles.offlineEmptyText]}>
      {OFFLINE_EMPTY_TEXT}
    </Text>
  </View>
);

/**
 * The list, and ONLY the list.
 *
 * The top-of-list `newThreadCard` and `newThreadBtn` are gone, not hidden:
 * composition lives at the bottom of the panel now, in `Composer`, and leaving
 * a second affordance here "just in case" would restore exactly the two-places
 * problem UI-3 exists to remove.
 */
const ThreadList: React.FC<{
  threads: Thread[];
  onOpen: (id: string) => void;
}> = ({ threads, onOpen }) => (
  <ScrollView style={styles.body} testID="correspondence-thread-list">
    {threads.length === 0 ? (
      <Text style={styles.empty}>No discussions yet.</Text>
    ) : (
      // No index-derived style (Story 5.4, D7): the alternate-row stripe of
      // Story 2.4 D6 is gone, and the avatar column gives the list its rhythm.
      threads.map(t => <ThreadRowItem key={t.id} thread={t} onOpen={onOpen} />)
    )}
  </ScrollView>
);

/**
 * `initials` and `nameWords` live in `format.ts` since Story 5.5 (C12): the
 * thread avatar in `MessageRow` shares them. They are NOT re-exported from
 * here (code review, 2026-09-11): a re-export kept one Story 5.4 test import
 * working at the cost of giving the panel a public export it does not own,
 * and of leaving two export sites for the next reader who greps for the
 * definition. The test imports from `format.ts` instead.
 */

/**
 * The header's second line (Story 5.4, AC 3; C11). `0` is not a count that
 * can be said: it is "not loaded yet", "the team call failed" or "access is
 * gone", and on every one of those the label alone is the honest line. A
 * label that is itself empty never leads with the separator.
 */
const headerSubtitle = (requestLabel: string, people: number): string => {
  if (people <= 0) return requestLabel;
  const count = `${people} ${people === 1 ? 'person' : 'people'}`;
  return requestLabel ? `${requestLabel} · ${count}` : count;
};

/** The leading avatar's box (Story 5.4, AC 6). Local: no other surface has one. */
const THREAD_AVATAR_SIZE = 28;

/**
 * One line of the mailbox (Story 2.4, D5; Story 5.4). Its own component
 * because a row has HOVER STATE on web (2.4 AC 11): with the card gone, the
 * border no longer says where a row ends, and in a flat list hover is how
 * you confirm what you are about to click. `onMouseEnter`/`onMouseLeave` and
 * `onFocus`/`onBlur` are spread only on web — react-native-web's
 * `TouchableOpacity` forwards them to its `View`, native never sees them, and
 * native keeps the press opacity it always had. Focus drives the same state
 * as the pointer (code review, 2026-09-10): the card border used to tell a
 * keyboard user where they were, and hover alone would have told only a
 * mouse user. Since Story 5.4 removed the stripe this fill is the ONLY fill a
 * row has (D7), so a keyboard user's position depends on it.
 * `Platform.OS` is read at render, not at module load, so a test can flip it.
 *
 * Two columns (Story 5.4, AC 5-10): a 28px avatar anchored on WHO LAST SPOKE,
 * then the text column — title row (title, date, dot slot) over a one-line
 * preview. The eye scans the list by person and by newness, not by title
 * length; and because the dot's 12px column is reserved whether or not a dot
 * is in it, nothing moves when a row is read.
 */
const ThreadRowItem: React.FC<{
  thread: Thread;
  onOpen: (id: string) => void;
}> = ({ thread: t, onOpen }) => {
  const [hovered, setHovered] = useState(false);
  const hoverProps =
    Platform.OS === 'web'
      ? {
          onMouseEnter: () => setHovered(true),
          onMouseLeave: () => setHovered(false),
          onFocus: () => setHovered(true),
          onBlur: () => setHovered(false),
        }
      : {};
  // A conversation cannot be created without a message (Story 1.3, D1), so
  // "no live message" is reachable one way only: every message was withdrawn.
  // The index still lists the row; the avatar shows the glyph and the preview
  // says `No messages` (C7). A live message with no author name is the same
  // glyph (D5): initials are never invented.
  //
  // ⚠️ "HAS A LIVE MESSAGE" IS NOT "HAS A BODY", AND READING IT THAT WAY WAS A
  // BUG. A message may carry files and NO text, which leaves `lastMessageBody`
  // empty for a message that plainly exists: the row showed the withdrawn-
  // everything glyph and the preview read `No messages` at a conversation
  // somebody had just posted a file into. The author is known in that case, so
  // the avatar must show their initials like any other message.
  //
  // ⚠️ AND IT IS NOT "HAS A BODY OR A FILE" EITHER (code review,
  // 2026-09-17, D3). That fixed the attachment leg and left a third one open:
  // `lastMessageBody` is `previewText(LEFT(body_html, 1024))`, so a body whose
  // first 1024 characters strip to no visible text -- deep nesting, a long
  // attribute chain surviving sanitisation, a truncated leading tag -- yields a
  // live message with no preview and no files, and the row said `No messages`
  // again. `No messages` means one specific thing, "every message was
  // withdrawn", and it must not be the fallback for "nothing to show".
  //
  // The wire settles it directly: `lastMessageAt` and `lastMessageAuthorId` are
  // written by the server for ANY live message and omitted with the rest of the
  // `lastMessage*` block when there is none (`correspondence.service.ts`, the
  // `lastMessageId !== undefined && lastMessageAuthorId !== undefined` gate).
  // Ask the question the field answers, rather than inferring it from content
  // that may legitimately be empty.
  const hasLiveMessage =
    t.lastMessageAuthorId !== undefined || t.lastMessageAt !== undefined;
  // What the preview can actually SHOW, which is a different question: a live
  // message may have nothing renderable, and then the snippet is blank rather
  // than a claim that no message exists.
  //
  // `.trim()` because truthiness is not the test: a body that previews to
  // whitespace (`<p>&nbsp;</p>`) is non-empty, won the branch, and rendered
  // `Matt: ` with nothing after it while two attachments went unmentioned.
  const previewBody = (t.lastMessageBody ?? '').trim();
  const previewText_ = previewBody || attachmentSummary(t);
  // #1599 (owner quick-dev 2026-10-26): the viewer's own @mention tokens in
  // the preview, bold in the body colour with no ground (owner quick-dev
  // 2026-10-27; the conversation's bubble keeps its red-on-pink look). Only
  // when the preview IS the body: the attachment summary is the app's words,
  // never a token.
  // `null` is a plain preview, which is also what any bad range answers.
  const ownSegments = previewBody
    ? previewSegments(t.lastMessageBody, previewBody, t.lastMessageOwnMentions)
    : null;
  const avatarInitials = hasLiveMessage
    ? initials(t.lastMessageAuthorName)
    : '';
  return (
    <TouchableOpacity
      onPress={() => onOpen(t.id)}
      style={[styles.threadRow, hovered ? styles.threadRowHover : null]}
      testID={`correspondence-thread-${t.id}`}
      {...hoverProps}
    >
      {/* ⚠️ FIRST CHILD, AND IT ADDS STATE WITHOUT REPLACING CONTENT.
              These rows have NO `accessibilityLabel` today, so VoiceOver reads
              their concatenated children: title, sender-prefixed preview and
              relative timestamp. Setting an explicit label would suppress ALL
              of it — including the preview, which is the very thing PRD FR55's
              rationale cites as the reason the count could be withdrawn.
              Sighted users would keep it and blind users would lose it.
              One vocabulary: the word is `unread`, never "unread messages".
              It is absolutely positioned, so it takes no column of the row. */}
      {/* #1599 Story 1.5: the mention's spoken state, a SECOND hidden
              sentence rendered BEFORE "Unread." so the order the A legend
              names is the DOM order. Same rule as above: no row label. */}
      {t.mentionsViewer === true ? (
        <VisuallyHiddenText
          testID={`correspondence-thread-${t.id}-mention-label`}
        >
          Mentions you.
        </VisuallyHiddenText>
      ) : null}
      {t.unread ? (
        <VisuallyHiddenText
          testID={`correspondence-thread-${t.id}-unread-label`}
        >
          Unread.
        </VisuallyHiddenText>
      ) : null}
      {/* ⚠️ THE AVATAR IS DECORATIVE TO ASSISTIVE TECH (Story 5.4, D4). The
              initials are derived, not content, and the name is already in the
              preview; a `Text` of "MT" as the first readable child would make
              every row announce "M T" first. Hidden the same way `UnreadDot`
              is. NO testID under the `correspondence-thread-` prefix: the E2E
              page object counts rows by it (2.4 AC 16). Two states: a person
              (the plain grey disc, `textSecondary` initials) or nobody (the
              dashed white glyph disc). Story 8.4 D5 briefly made the person
              case a filled disc in one of eight hues hashed from the last
              sender's id; the owner reversed that on 2026-09-21 and the
              palette is deleted, so there is no per-row fill expression left
              here. Read/unread is NOT on the avatar (8.4 D5, NOT reversed):
              the title weight and the badge carry it. */}
      <View
        style={[
          styles.threadAvatar,
          avatarInitials === '' ? styles.threadAvatarEmpty : null,
        ]}
        accessibilityElementsHidden
        importantForAccessibility="no-hide-descendants"
      >
        {avatarInitials === '' ? (
          <MessageSquare size={13} color={colors.textSecondary} />
        ) : (
          <Text style={styles.threadAvatarText}>{avatarInitials}</Text>
        )}
      </View>
      <View style={styles.threadText}>
        {/* ⚠️ A PLAIN DOT, TRAILING, IN FLOW (Story 2.4, D1). The owner's
              2026-08-25 words were "for the card it should be on the top
              right, wher the rounded border is", and then, explicitly,
              **"Card keeps plain dot"**. The card is gone (Story 2.4, D5) and
              with it the corner; the POSITION did not move — the dot is still
              trailing, which is what `ux-1069-request-correspondence.md:189`
              always said — only the RATIONALE ("where the rounded border is")
              retired with the radius.
              ⚠️ SO THE "New" BADGE IS NOT A PATTERN THAT WANTS SPREADING. It
              belongs to the space TABS and to nothing else. Extending it to
              this row, to the request-list row or to the launcher pill to
              "finish the job" would be undoing a decision, not completing one.
              ⚠️ Unread is said THREE ways per row visually since Story 5.4 —
              the dot (structural), the title weight (typographic) and the
              preview colour (D2, the fourth carrier counting the spoken
              "Unread." above) — and never by colour alone. That is not the
              "said too many ways" Story 5.2 removed: that was a second element
              in a second place saying the same sentence. */}
        <View style={styles.threadTop}>
          <Text
            style={[
              styles.threadTitle,
              // `t.unread ?`, never `=== false`: `undefined` means the
              // read-state lookup FAILED, and an unknown row must fall to
              // the READ weight (Story 2.4 AC 3), as the dot falls to nothing.
              t.unread ? styles.threadTitleUnread : styles.threadTitleRead,
            ]}
            numberOfLines={1}
          >
            {t.title}
          </Text>
          {/* #1599 Story 1.5 (frame A1): the red `@`, between the title and
                the time, while `mentionsViewer === true`. A FIFTH carrier on
                this row, and the only viewer-relative one: it says "for you"
                where the dot, the weight and the preview colour say "new".
                Hidden from assistive tech like the dot; "Mentions you." above
                speaks it. `flexShrink: 0`, so the title is still the only
                thing that gives way at the 360pt dock. */}
          {t.mentionsViewer === true ? (
            <Text
              style={styles.threadMentionGlyph}
              accessibilityElementsHidden
              importantForAccessibility="no-hide-descendants"
              testID={`correspondence-thread-${t.id}-mention`}
            >
              @
            </Text>
          ) : null}
          {/* ⚠️ `t.messageCount` USED TO BE HERE AND IS REMOVED ON THE
                OWNER'S EXPLICIT INSTRUCTION, on evidence he generated himself
                (2026-08-25). Using the panel against real data he read that
                integer as an UNREAD count and reported working behaviour as a
                bug — "clicked a thread showing 3, refreshed, it came back". It
                came back because it is the TOTAL, and reading a thread does not
                change how many messages it contains. Nothing was broken; the
                position was. A competent user who personally specified this
                feature misread an integer in a card's top-right corner, because
                that is where every mail client puts an unread badge.
                A one-message thread and a two-hundred-message thread are
                visually identical, which was the stated cost and is accepted:
                the row still carries the sender-prefixed preview and the date.
                ⚠️ Re-introducing ANY count here is a PRD-level decision
                (r8), not a UI tidy-up.
                The DATE takes the slot before the dot (Story 2.4, D4),
                collapsed to time / weekday / date the way a mailbox does it,
                because the absolute form was a ~90px block that ate the title
                at the 360pt dock minimum. Since Story 5.4 it is a STABLE
                column: `flexShrink: 0` and tabular digits (AC 7). NO testID on
                it: the E2E page object counts rows by the
                `correspondence-thread-` prefix, and a date node under that
                prefix would join every count (2.4 AC 16). */}
          {t.lastMessageAt ? (
            <Text
              style={styles.threadWhen}
              // The screen reader hears the ABSOLUTE form (code review,
              // 2026-09-10): "Tue" is legible to a sighted reader because of
              // where the row sits in a newest-first list, and a listener has
              // neither that position nor the year. Same `formatWhen` the
              // bubbles use, so the panel's two date forms are one fact.
              accessibilityLabel={formatWhen(t.lastMessageAt)}
            >
              {formatListWhen(t.lastMessageAt)}
            </Text>
          ) : null}
          {/* ⚠️ THE SLOT IS THE ROW'S, NOT THE DOT'S (Story 5.4, D6; AC 8).
                Always rendered, sized by the exported `UNREAD_DOT_SIZE` so the
                title and date sit in the same place read or unread. `UnreadDot`
                still renders NOTHING when read — a muted or zero-opacity dot in
                here would make colour the only carrier (WCAG 1.4.1). An empty
                `View` is nothing to a screen reader. */}
          <View style={styles.threadDotSlot}>
            <UnreadDot
              unread={t.unread}
              testID={`correspondence-thread-${t.id}-unread`}
            />
          </View>
        </View>
        {/* Preview is server-supplied (Story 1.2): the latest LIVE message's
              text and its author's name, resolved on the server. First name
              only, as before, beside an avatar that shows the same person's
              initials (C8). ONE line (Story 5.4, AC 10); weight is `regular`
              in both states and COLOUR steps with unread (2.4 D3's fallback,
              taken 2026-09-11). With no live message the line says so, in
              `textSecondary`, never `textMuted` (D3: #999 fails 1.4.3 on
              every ground in the panel). */}
        {/* ⚠️ BRANCHED ON `hasLiveMessage`, THE SAME EXPRESSION THE AVATAR
              USES (code review, 2026-09-17). These two answered "is there a
              live message?" differently -- the avatar with
              `Boolean(body) || attachmentCount > 0`, the preview with
              `body || attachmentSummary(t)` -- and they agreed only because
              `attachmentSummary` returns `''` exactly when the count is 0,
              which `format.test.ts` pins as "THE EMPTY STRING IS THE
              CONTRACT". Give that helper any reason to say something at a
              count it currently ignores and the avatar shows initials while
              the preview says `No messages`. One expression, computed once,
              cannot disagree with itself. */}
        {hasLiveMessage ? (
          <Text
            style={[
              styles.threadSnippet,
              t.unread ? styles.threadSnippetUnread : styles.threadSnippetRead,
            ]}
            numberOfLines={1}
          >
            {/* #1599 Story 4.1 (frame H2): the label follows the first name,
                before the colon; the one tail ellipsis cuts the preview
                first. The avatar's initials above stay the person's own. */}
            {nameWords(t.lastMessageAuthorName).length > 0
              ? `${withConfigTeamLabel(
                  nameWords(t.lastMessageAuthorName)[0],
                  t.lastMessageAuthorConfigTeam,
                )}: `
              : ''}
            {ownSegments
              ? ownSegments.map((segment, i) =>
                  segment.own ? (
                    <Text
                      key={`own-${i}`}
                      style={styles.threadSnippetOwnMention}
                      testID={`correspondence-thread-${t.id}-own-mention`}
                    >
                      {segment.text}
                    </Text>
                  ) : (
                    segment.text
                  ),
                )
              : previewText_}
          </Text>
        ) : (
          <Text
            style={[styles.threadSnippet, styles.threadSnippetRead]}
            numberOfLines={1}
          >
            No messages
          </Text>
        )}
      </View>
    </TouchableOpacity>
  );
};

const styles = StyleSheet.create({
  backdrop: {
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'flex-end',
    backgroundColor: 'rgba(0,0,0,0.35)',
  },
  backdropFill: { flex: 1 },
  /**
   * `PanelKeyboardColumn`'s two levels (defect 2). A ROW, deliberately: it
   * stands between the backdrop row and the panel, and the panel has always
   * taken its height from being a stretched child of a row. A column here
   * would make the panel size to its content instead, and `panelOverlay`
   * cannot simply be given `flex: 1` to compensate — on a row's main axis that
   * resolves to `flexBasis: 0%` and beats the width, which is the trap the
   * `panel` comment above already records.
   */
  keyboardColumn: { flex: 1, flexDirection: 'row' },
  /**
   * No `flex` here on purpose. `flex: 1` expands to `flexBasis: 0%`, which wins
   * over `width` on the main axis of a row — so a docked panel with a 400px
   * width still measured zero and rendered nothing. Each presentation sets its
   * own width, and the cross axis stretches by default.
   */
  panel: {
    backgroundColor: colors.backgroundPrimary,
    borderLeftWidth: isWeb ? 1 : 0,
    borderLeftColor: colors.borderPanel,
  },
  /**
   * ⚠️ `maxWidth` IS LOAD-BEARING, NOT DEFENSIVE (defect 1, 2026-09-15).
   *
   * The 460 is a rigid width with `flexShrink: 0`, inside a backdrop row that
   * is `justifyContent: 'flex-end'`. On a web viewport NARROWER than 460 the
   * `backdropFill` beside it collapses to zero, the panel refuses to shrink,
   * and the surplus hangs off the LEFT edge of the device — the panel is
   * pinned right, so that is the only direction the overflow can go.
   *
   * The cap makes the panel full-bleed once 460 no longer fits (owner decision
   * D1), which is what native has always done. Below that width the backdrop
   * has no area left to press, so the header's close control is the way out;
   * that too is native's behaviour, and the two platforms converging is the
   * point rather than the cost.
   *
   * It is a CAP and not a computed width on purpose. A width derived from
   * `useWindowDimensions` would re-render the whole panel on every tick of a
   * resize drag to reproduce what one CSS declaration does continuously and
   * for free. Native is unaffected either way: `'100%'` is already at the cap.
   */
  panelOverlay: {
    width: isWeb ? 460 : '100%',
    maxWidth: '100%',
    flexGrow: 0,
    flexShrink: 0,
  },
  /**
   * Fills the dock (#1069 UI-4). It used to pin `DOCK_PANEL_WIDTH` so the
   * content would not reflow while the dock shell animated its own width from
   * 0 — but once the dock became draggable, a fixed width meant the panel
   * stayed 400px inside a container the user had widened, leaving dead space.
   *
   * The no-reflow property is preserved, and now belongs to the dock: the
   * animating shell wraps a STABLE-width sleeve, so `100%` here resolves
   * against a width that does not change during the transition.
   */
  panelDocked: {
    width: '100%',
    flexGrow: 0,
    flexShrink: 0,
    flexBasis: 'auto',
    height: '100%',
  },
  // ⚠️ THE 3px AUDIENCE-COLOURED TOP BORDER (`panelInternal` orange /
  // `panelShared` warning) WAS REMOVED ON 2026-09-08 at the owner's request,
  // after Story 5.1 had settled the palette. It was the last piece of chrome
  // that said the audience by colour alone.
  //
  // ⚠️ THIS COMMENT SAID "four ways — icon, label, HINT, and the composer's
  // notice" UNTIL STORY 7.1 (2026-09-18), AND IT CARRIES A CONFORMANCE CLAIM,
  // so the count is corrected rather than left to age. D5 deleted the visible
  // hint. What is on screen now is the tab's ICON and its LABEL, both
  // non-colour, which is what NFR29 asks for ("more than colour alone", not a
  // count) — so NFR29 still holds without the top border. The hint survives in
  // `accessibilityLabel` (D7) and the composer's notice is a COMPOSE-TIME cue
  // only: `Composer.tsx`'s `idle` branch renders no notice, so on the resting
  // thread list the two visible cues are the whole of it. Accepted knowingly;
  // see D5 in the story.

  /**
   * Three direct children, top-aligned (Story 5.4, AC 1): the chevron
   * (thread view only), the title stack, the close control. The stack is the
   * only one allowed to shrink; both controls are `flexShrink: 0` with a fixed
   * basis, which is what stops a long subject pushing the close button on web
   * (C2: the old wrapper had `flex: 1` and no `minWidth: 0`).
   */
  header: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 6,
    paddingHorizontal: 14,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: colors.borderPanel,
  },
  headerTitleWrap: { flex: 1, minWidth: 0 },
  /**
   * The rename editor (#1069 Story 7.3, D11). The field takes the row and the
   * two controls sit beside it; nothing here shrinks the field to nothing
   * because `flex: 1` plus `minWidth: 0` is the same rule the title stack uses.
   */
  renameRow: { flexDirection: 'row', alignItems: 'center', gap: 6 },
  /** `Composer`'s `titleInput` at the header's own 15px (D11). */
  renameInput: {
    flex: 1,
    minWidth: 0,
    borderWidth: 1,
    borderColor: colors.inputBorder,
    borderRadius: 6,
    paddingHorizontal: 8,
    paddingVertical: 4,
    fontSize: 15,
    fontWeight: '700',
    color: colors.textPrimary,
    backgroundColor: colors.backgroundPrimary,
  },
  renameAction: {
    flexGrow: 0,
    flexShrink: 0,
    minHeight: spacing.minimumTapTarget,
    paddingHorizontal: 6,
    alignItems: 'center',
    justifyContent: 'center',
  },
  renameActionText: {
    fontSize: 13,
    fontWeight: '700',
    color: colors.primaryStrong,
  },
  /** D8: the rename's OWN line, directly under the title. */
  renameError: { fontSize: 11, color: colors.error, marginTop: 2 },
  /**
   * The pencil (AC 13). 24×24 on web with a 6pt `hitSlop` either side, which
   * clears SC 2.5.8's 24 CSS px with room to spare; `spacing.minimumTapTarget`
   * on native, where there is no hover to reveal it and it is always visible.
   *
   * ⚠️ `width`/`height` are NOT here: they are a per-render `Platform.OS` read
   * in the component body (`pencilSize`). A `StyleSheet.create` read happens at
   * module scope, which under test is frozen to `'ios'` (patch P4).
   */
  renameBtn: {
    flexGrow: 0,
    flexShrink: 0,
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: -2,
  },
  headerTitle: { fontSize: 16, fontWeight: '700', color: colors.textPrimary },
  headerTitleThread: { fontSize: 15 },
  /**
   * `textMuted` on the request label is an EXCEPTION Story 5.4 recorded and
   * did not widen (D3): it has it today, and no new text in the panel takes it.
   */
  headerSub: { fontSize: 11, color: colors.textMuted, marginTop: 1 },
  backBtn: {
    flexGrow: 0,
    flexShrink: 0,
    flexBasis: 24,
    width: 24,
    height: 24,
    alignItems: 'center',
    justifyContent: 'center',
  },
  /**
   * A 32×32 target with the 18px X centred in it (AC 4). The negative top and
   * right margins pull the box up into the padding so the header's height and
   * the X's visual position are what they were with the old `padding: 4`.
   */
  closeBtn: {
    flexGrow: 0,
    flexShrink: 0,
    flexBasis: 32,
    width: 32,
    height: 32,
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: -6,
    marginRight: -8,
  },

  /**
   * Content-width tabs in a row, NOT two `flex: 1` halves (#1069 Story 7.1,
   * D2). The halves are what made the old fill read as a pair of buttons
   * rather than as tabs, and they spent all ~170px of a 360px docked panel on
   * two slabs. `alignItems: 'flex-end'` puts both underlines on one baseline.
   *
   * ⚠️ `paddingHorizontal` IS 10, NOT 14, AND THE 4 IS NOT A ROUNDING ERROR.
   * The panel has ONE left edge at 14 — `header`, `singleSpaceNote`,
   * `errorBar`, `threadRow` and `TeamStrip`'s `summary` and `body` all use it.
   * But a tab carries its own `paddingHorizontal` of `spacing.xs` (4) INSIDE
   * the row, so a row padded to 14 puts the lock icon at 18 and the tab bar
   * alone is indented from every band above and below it (owner, 2026-09-18,
   * from a screenshot of the built tabs). 10 + 4 lands the icon on 14 with
   * everything else, and leaves the underline bleeding 4px wider than its
   * label, which is what a tab underline is supposed to do.
   */
  tabs: {
    borderBottomWidth: 1,
    borderBottomColor: colors.borderPanel,
  },
  /**
   * ⚠️ THE ROW SCROLLS HORIZONTALLY, AND THAT IS NOT DECORATION — IT IS WHAT
   * KEEPS BOTH TABS REACHABLE AT 200% TEXT (code review, 2026-09-18).
   * Dropping `flex: 1` (D2) made the tabs content-width and removed the shrink
   * it had been providing; nothing replaced it. Both RN and RNW default a view
   * to `flexShrink: 0` and do not clip, so at `fontScale: 2` — SC 1.4.4's
   * plain requirement, not an exotic setting — the row measures about 416px
   * against a 320pt device and the Shared tab's press target leaves the
   * screen. Worked from the story's own width table: the text parts (label
   * ~48, badge ~32) double while the icon and paddings do not.
   *
   * `flexShrink` is this repo's usual answer to an overflow
   * (`RequestDeliverablesEditor.tsx:184`) and is the WRONG one here: it buys
   * reachability by ellipsizing "Vertiv" / "Shared", and after D5 spent the
   * hint those two words are one of only two visible cues NFR29 still rests
   * on. Capping Dynamic Type would bound the row too, but `maxFontSizeMultiplier`
   * appears NOWHERE in `src/` and a confidentiality surface is no place to
   * introduce that policy. Scrolling is the one option that compresses
   * neither the words nor the target.
   *
   * At `fontScale: 1` this is pixel-identical to the reviewed design: the
   * content is narrower than the panel, so nothing scrolls and nothing looks
   * scrollable (`showsHorizontalScrollIndicator={false}`).
   *
   * ⚠️ THE -1 OVERLAP MOVED FROM THE TAB TO THIS SCROLLER, AND IT HAD TO.
   * F1's fix hangs the selected underline 1px BELOW its box so it lands on the
   * rule `styles.tabs` draws; a child that overflows a ScrollView's content
   * box is exactly what a ScrollView clips, so leaving it on the tab would
   * have silently undone F1 on the surface F1 was filed for. The scroller's
   * own box overlaps its parent's border instead — no child overflows
   * anything. `flexGrow: 0` keeps it out of the measured region the
   * composer's arithmetic reads (`composerRegionHeight`).
   */
  tabScroller: {
    flexGrow: 0,
    marginBottom: -1,
  },
  tabRow: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    gap: spacing.md,
    paddingHorizontal: 10,
  },
  /**
   * ⚠️ `minHeight` IS LOAD-BEARING, AND IT IS THE ONE THING THIS LAYOUT DOES
   * NOT INHERIT FROM `oppSubTabStyles`. Losing the hint row took the tab from
   * two lines to one; padding alone would leave it **34px**, under the 44pt
   * touch target that the two-line layout had been meeting by accident. The
   * tab is 44px now, four pixels SHORTER than the 48px it replaced — the safe
   * direction for the composer's arithmetic (see `composerLayout.ts`), and
   * still a legal target. (`minimumTapTarget`'s own docstring records that
   * Android's 48dp sits above 44; that floor is the project's, unchanged
   * here.)
   *
   * The transparent 2px border is what makes selection cost no layout, and
   * the negative `marginBottom` lands the selected underline ON the bar's own
   * rule instead of floating above it. Mostly copied from `oppSubTabStyles.tab`
   * (`src/request/steps/opportunityinformation/OpportunityInformation.tsx:75-86`).
   *
   * ⚠️ THE -1 OVERLAP IS NOT HERE ANY MORE — IT IS ON `tabScroller`, AND THE
   * REASON IT MOVED IS IN THAT DOCBLOCK. It was `marginBottom: -1` on this
   * style, itself a correction to `oppSubTabStyles`' `-StyleSheet.hairlineWidth`
   * (which is `1 / PixelRatio`: 1 on web, **0.33 on a 3x iPhone**, so the
   * copied value cancelled the rule on web and left two thirds of a grey line
   * under the orange on native — a doubled edge visible only on the surface it
   * is NOT checked on). That correction stands; only the style it sits on
   * changed, because a ScrollView clips a child that hangs below its content
   * box and this tab is now inside one.
   */
  tab: {
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.xs,
    minHeight: spacing.minimumTapTarget,
    justifyContent: 'center',
    borderBottomWidth: 2,
    borderBottomColor: 'transparent',
    backgroundColor: 'transparent',
  },
  /**
   * Selection is a 2px `primaryStrong` UNDERLINE plus a `fontWeight` step plus
   * the brand tint, exposed as `accessibilityState.selected`. **No fill.**
   *
   * ⚠️ READ THE HISTORY BEFORE REVERSING THIS AGAIN — it has moved four times
   * and twice in opposite directions, which is why it is written down:
   *   1. a `surfaceHover` grey fill (~1.04:1) — removed in
   *      `spec-panel-top-edge-cleanup` for stacking a third grey against the
   *      tinted audience band beneath;
   *   2. a 2px `colors.primary` underline (2.84:1) — removed 2026-09-08, the
   *      owner asking for "the orange to go";
   *   3. a `surfaceMessage` blue fill (**1.22:1**) — the state this story
   *      found, and the worst of the four. A fill that pale is not an
   *      indicator; on a greyscale rendering it is very nearly nothing;
   *   4. this: underline + weight + tint, owner 2026-09-18 (Story 7.1, D2).
   *
   * Step 4 REVERSES step 2 deliberately and by the same owner, who has since
   * used it. The reversal is possible only because the orange is a different
   * orange: `primaryStrong` `#C2410C` is **5.18:1**, where `colors.primary`
   * `#FF6B35` is the same 2.84:1 that got the underline deleted the first
   * time. See `colors.ts`'s three-orange docstring before reaching for a
   * brand colour here.
   *
   * ⚠️ THE WEIGHT STEP IS NOT DECORATION. FR75 asks for an indicator that is
   * not colour alone. `medium` → `semibold` and the underline's presence are
   * the two cues that survive greyscale; the tint carries almost nothing
   * (5.18 against 5.74 is near-equal luminance). The old design stepped
   * NEITHER — `fontWeight` was `'600'` in both states.
   *
   * ⚠️ THE UNREAD BADGE'S GROUND MOVED WITH THE FILL: white in both states
   * now (**4.73:1**, up from 3.87:1 on the blue). `UnreadBadge.tsx` and
   * `colors.ts` say so too, and `colors.test.ts` still pins the badge against
   * `surfaceMessage` because bubbles and the unread thread avatar are still
   * that colour — only the TAB claim was retired.
   */
  tabActive: {
    borderBottomColor: colors.primaryStrong,
  },
  tabTop: { flexDirection: 'row', alignItems: 'center', gap: 5 },
  /**
   * `textSecondary` (**5.74:1**), not `textMuted` (`#999`, **2.85:1**, which
   * failed SC 1.4.3 and is filed repo-wide at `deferred-work.md:2649`). Story
   * 7.1 moved the tab bar off it and left every other call site alone — the
   * sweep is still open and still somebody else's story. `colors.textMuted`'s
   * own value is NOT edited; it is pinned by a drift assertion and used across
   * the app.
   */
  tabLabel: {
    fontSize: fontSize.formLabel,
    lineHeight: lineHeight.formLabel,
    fontWeight: fontWeight.medium,
    color: colors.textSecondary,
  },
  tabLabelActive: {
    color: colors.primaryStrong,
    fontWeight: fontWeight.semibold,
  },

  singleSpaceNote: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 14,
    paddingVertical: 9,
    borderBottomWidth: 1,
    borderBottomColor: colors.borderPanel,
    backgroundColor: colors.surfacePanelMuted,
  },
  singleSpaceText: { fontSize: 12, color: colors.textSecondary },

  loading: { flex: 1, alignItems: 'center', justifyContent: 'center' },
  errorBar: {
    paddingHorizontal: 14,
    paddingVertical: 10,
    backgroundColor: colors.surfaceWarningMuted,
    borderBottomWidth: 1,
    borderBottomColor: colors.warning,
  },
  errorTitle: {
    fontSize: 11,
    fontWeight: '700',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    color: colors.textOnWarningMuted,
  },
  errorBody: { fontSize: 12, color: colors.textOnWarningMuted, marginTop: 3 },
  /** #1599 Story 1.4: the remove-and-send action, the panel's link voice. */
  errorActionHit: { alignSelf: 'flex-start', marginTop: 6, paddingVertical: 2 },
  errorAction: {
    fontSize: 12,
    fontWeight: '600',
    color: colors.textLink,
    textDecorationLine: 'underline',
  },

  /**
   * The measured region (#1069 UI-8). `flex: 1` so it takes exactly what the
   * panel's fixed chrome leaves — which is what makes its height independent of
   * everything inside it, and therefore of the drag.
   *
   * It replaces the `flex: 1` the list used to contribute directly to this
   * column. The list still has its own `flex: 1` INSIDE here, so the rendered
   * layout is unchanged: region takes the leftover space, composer takes what
   * it needs, list takes the rest.
   */
  composerRegion: { flex: 1 },
  /**
   * The disclosure row. 14 to match the bands above it (Story 7.1's F2
   * settled the panel's left edge), a real tap target, and a hairline below
   * so it reads as a band rather than as floating text.
   */
  /**
   * Story 7.5, D10: the toggle and its Save-all sibling, in one row. The
   * hairline moved here from the toggle so it runs under both; the 2px
   * progress bar sits on this row's bottom edge, absolutely, and borrows no
   * layout.
   */
  filesRow: {
    flexDirection: 'row',
    alignItems: 'center',
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: colors.borderPanelSoft,
  },
  filesToggle: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    paddingHorizontal: 14,
    minHeight: spacing.minimumTapTarget,
  },
  filesSaveButton: {
    width: spacing.minimumTapTarget,
    height: spacing.minimumTapTarget,
    alignItems: 'center',
    justifyContent: 'center',
  },
  /**
   * The bar (D10, D12): `MessageList`'s `pendingProgressTrack/Fill` tokens
   * (2px, radius 1, `borderPanelSoft` under `info`), copied rather than
   * imported. Inset 14 to the row's text edge.
   */
  filesSaveTrack: {
    position: 'absolute',
    left: 14,
    right: 14,
    bottom: 0,
    height: 2,
    borderRadius: 1,
    backgroundColor: colors.borderPanelSoft,
    overflow: 'hidden',
  },
  filesSaveFill: {
    height: 2,
    borderRadius: 1,
    backgroundColor: colors.info,
  },
  /**
   * ⚠️ COLOUR CARRIES NOTHING HERE (NFR29). Open and closed differ by the
   * chevron's direction and by whether the strip is in the tree; the count is
   * text. Nothing in this row is a new colour cue and nothing removes one.
   */
  filesToggleText: { flex: 1, fontSize: 12, color: colors.textPrimary },

  body: {
    flex: 1,
    // Web only (P4): a wheel that reaches the end of this list must not chain
    // into the page and scroll the request. Style prop, not a ref effect:
    // react-native-web 0.21.2 supports `overscrollBehavior` outright
    // (expanded to the -x/-y longhands; executed check on `atomic()`).
    ...Platform.select({ web: { overscrollBehavior: 'contain' } as any }),
  },
  /**
   * The list has NO inset of its own (Story 2.4, AC 8): the horizontal
   * padding lives on the row, so the separator runs edge to edge and the text
   * does not, and there is no `gap` — 10pt of dead air between separators
   * reads as a bug. The empty state carries its own inset for the same reason.
   */
  empty: {
    fontSize: 13,
    color: colors.textMuted,
    textAlign: 'center',
    paddingVertical: 24,
    paddingHorizontal: 12,
  },
  /** The offline empty state: the icon above `empty`'s sentence (#1069). */
  offlineEmpty: {
    flex: 1,
    alignItems: 'center',
    paddingTop: 24,
  },
  /**
   * ⚠️ `textSecondary`, NOT `empty`'s `textMuted` (#1069 offline state):
   * #999 on white is ~2.8:1, under SC 1.4.3's 4.5:1 at 13px; #666 is ~5.7:1.
   */
  offlineEmptyText: { paddingTop: spacing.sm, color: colors.textSecondary },
  /**
   * Where the composer stands while offline: `empty`'s tones on a shorter
   * rhythm, with the composer's own top rule so the region's bottom edge does
   * not move.
   */
  offlineComposerLine: {
    // `textSecondary` for contrast, as `offlineEmptyText` says.
    color: colors.textSecondary,
    paddingVertical: 12,
    borderTopWidth: 1,
    borderTopColor: colors.borderPanelSoft,
  },

  /**
   * A line in a list, not a card in a stack (Story 2.4, D5; AC 7, 12), and
   * since Story 5.4 a TWO-COLUMN line (AC 5): avatar, then text, 10 apart. No
   * border, no radius, NO FILL OF ITS OWN; a 1px separator on EVERY row
   * including the last — email clients keep it, and without it the list
   * looks truncated mid-scroll. The separator is `borderPanelSoft`, not
   * `borderPanel` (2.4 D6): a rule between every row at panel-outline weight
   * reads as a grid. `minHeight: 44` is the touch floor, explicit so a test
   * can hold it. The alternate-row stripe 2.4 D6 added is GONE (5.4 D7): the
   * avatar column gives the list its rhythm, and hover/focus is the only fill.
   */
  threadRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    paddingVertical: 12,
    paddingHorizontal: 14,
    minHeight: 44,
    borderBottomWidth: 1,
    borderBottomColor: colors.borderPanelSoft,
  },
  /**
   * Web only, set by `ThreadRowItem`; the ONLY fill a row ever has (5.4 D7).
   * A HUE shift, not a grey (2.4 D7): `surfaceHover` vanished against the
   * stripe the list had then, and the blue was kept when the stripe went.
   * `textSecondary` is 4.96:1 on it (pinned by test). Fallback on record if
   * it reads too faint: `surfaceMessage`.
   */
  threadRowHover: { backgroundColor: colors.surfaceSelectSelected },
  /**
   * The leading avatar (Story 5.4, AC 6; repainted by Story 8.4 D5 and then
   * partly REVERSED by the owner on 2026-09-21): 28px, round. TWO states,
   * down from three: a person is the plain grey disc — `surfacePanelMuted`
   * fill, `textSecondary` initials (**5.16:1**), NO border; nobody (no live
   * message, or no author name) is the white dashed disc with a
   * `textSecondary` glyph.
   *
   * ⚠️ 8.4 D5's eight hashed hues with white initials are GONE by owner
   * ruling, and the palette and its hash are deleted, so the fill is a style
   * property again rather than a per-row expression. The ruling is the
   * reason; the hues measured fine and were not wanted.
   *
   * ⚠️ BORDERLESS IS A SEPARATE RULING, not a leftover of 8.4. The pre-8.4
   * disc had a 1px ring; the owner chose borderless when shown that a muted
   * disc on the white panel is a 1.11:1 edge, under the 3:1 shape floor. That
   * is accepted and ledgered, not a defect to re-ring. The initials carry the
   * text obligation.
   *
   * Read/unread used to step the fill (grey / selection blue) and the
   * initials (grey / dark); that is gone (8.4 D5, NOT reversed) and the row's
   * unread state lives on the title weight and the `New` badge. The same disc
   * at 24 sits beside a message in `MessageRow` (`28 * 6 === 24 * 7`, pinned).
   */
  threadAvatar: {
    flexGrow: 0,
    flexShrink: 0,
    flexBasis: THREAD_AVATAR_SIZE,
    width: THREAD_AVATAR_SIZE,
    height: THREAD_AVATAR_SIZE,
    borderRadius: THREAD_AVATAR_SIZE / 2,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.surfaceAvatar,
  },
  threadAvatarEmpty: {
    backgroundColor: colors.backgroundPrimary,
    borderWidth: 1,
    borderColor: colors.borderPanel,
    borderStyle: 'dashed',
  },
  /**
   * `textSecondary` on the muted fill, **5.16:1** (owner ruling 2026-09-21,
   * superseding Story 8.4 D5). 8.4 had these `textLight`, which was white on
   * a hue; with the hue gone white would be 1.11:1 on the muted disc.
   */
  threadAvatarText: {
    fontSize: 11,
    fontWeight: fontWeight.bold,
    color: colors.textPrimary,
  },
  /** The text column (AC 7): it is the one that gives way, hence `minWidth: 0`. */
  threadText: { flex: 1, minWidth: 0, gap: 2 },
  threadTop: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  threadTitle: {
    flex: 1,
    minWidth: 0,
    fontSize: 14,
    color: colors.textPrimary,
  },
  /**
   * Unread is carried by TYPE WEIGHT as well as by the dot (Story 2.4, D2).
   * `ux-1069-request-correspondence.md:189` specified it and it was never
   * built — and what IS built inverts that line's label: it said "medium
   * when unread", and here medium is the READ weight. The planning line now
   * records the supersession. Read 500 / unread 700 — a two-step delta, because Roboto on many
   * Android devices renders 600 and 700 as the same face, so a 600/700 pair
   * would pass in Jest and vanish on hardware. Read rows are therefore
   * LIGHTER than the 600 they were; the owner accepted that knowingly. The
   * colour is `textPrimary` in both states (AC 14): weight is the carrier.
   */
  threadTitleRead: { fontWeight: fontWeight.medium },
  threadTitleUnread: { fontWeight: fontWeight.bold },
  /**
   * The preview is `regular` in BOTH states and COLOUR carries unread
   * (Story 5.4, D2: Story 2.4 D3's recorded fallback, taken by the owner on
   * 2026-09-11 — the bolded preview read heavy beside the bold title). The
   * dot and the title weight are the other carriers, so unread is never said
   * by colour alone. No weight variant remains here on purpose.
   */
  threadSnippet: {
    fontSize: 12,
    lineHeight: 17,
    fontWeight: fontWeight.regular,
  },
  threadSnippetRead: { color: colors.textSecondary },
  threadSnippetUnread: { color: colors.textPrimary },
  // The viewer's own @mention in the preview (#1599, owner quick-dev
  // 2026-10-27): bold body colour, no background, read or unread alike.
  threadSnippetOwnMention: { color: colors.textPrimary, fontWeight: '700' },
  /**
   * `textSecondary`, NOT `textMuted` (Story 2.4, AC 6). `textMuted` is #999,
   * 2.85:1 on white — it failed WCAG 1.4.3 from the day it shipped, invisibly,
   * as the last line of a card nobody looked at. It cannot be promoted into
   * the title row failing. Pinned with the Story 5.1 harness, on both grounds
   * the row can have (resting and web hover).
   *
   * A STABLE column (Story 5.4, AC 7): it never shrinks for the title, and
   * tabular digits keep "3:42 PM" and "11:05 AM" the same width.
   */
  threadWhen: {
    fontSize: 11,
    color: colors.textSecondary,
    flexShrink: 0,
    fontVariant: ['tabular-nums'],
  },
  /**
   * #1599 Story 1.5: the row's `@`. `colors.error`, the dot's red (owner
   * ruling 2026-10-22): a glyph owes 3:1 (SC 1.4.11) and `error` is 3.55:1 on
   * white and 3.06:1 on the row's hover/focus fill, `surfaceSelectSelected`
   * (both pinned in `colors.test.ts`).
   * No `lineHeight`, as `threadTitle` sets none, so the two share a baseline
   * box in the centred row.
   */
  threadMentionGlyph: {
    fontSize: 13,
    fontWeight: '700',
    color: colors.error,
    flexShrink: 0,
  },
  /**
   * The dot's column, reserved by the ROW (Story 5.4, D6; AC 8), so the title
   * and date do not move when a row is read. Sized by the dot's own export,
   * never a retyped 12.
   */
  threadDotSlot: { width: UNREAD_DOT_SIZE, flexShrink: 0 },

  // The message-row styles that used to live here moved to MessageRow.tsx with
  // the render they belong to, and the composer / new-thread styles moved to
  // Composer.tsx with theirs. Nothing composition-shaped is left in this file.
});
